import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CATEGORIES,
  BLOG_POSTS,
  BLOGS,
  ARTICLE,
  titleToSlug,
  resolveArticle,
} from "../Constants/BlogsConst";
import { REACTION_TYPES, REACTION_LABELS, REACTION_ICONS, CURRENT_USER_ID, getMemberById } from "../Constants/CommunityConst";
import { CommentsInline, Overlay, commentStore, useCommentCount, ReportPostModal, SocialLinksOverlay, socialPlatformById, socialHref, SOCIAL_PLATFORMS } from "./Community";
import "../Styles/BlogsStyle.css";
import "../Styles/CommunityStyle.css";

/* ------------------------------------------------------------
   SAVED BLOGS STORE (shared + persisted)
   Single source of truth for the bookmark/save action across the
   whole Blogs experience. Saving a blog anywhere drops it into the
   Published → Saved tab (and nowhere else) and survives reloads.
   ------------------------------------------------------------ */
const SAVED_BLOGS_KEY = "ihn-saved-blog-ids";
const loadSavedBlogIds = () => {
  try {
    const raw = localStorage.getItem(SAVED_BLOGS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const savedBlogSubscribers = new Set();
let savedBlogIdsStore = loadSavedBlogIds();
export const savedBlogsStore = {
  getIds: () => savedBlogIdsStore,
  toggle: (id) => {
    const next = new Set(savedBlogIdsStore);
    const wasSaved = next.has(id);
    if (wasSaved) next.delete(id);
    else next.add(id);
    savedBlogIdsStore = next;
    try {
      localStorage.setItem(SAVED_BLOGS_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    savedBlogSubscribers.forEach((fn) => fn());
    return wasSaved;
  },
  subscribe: (fn) => {
    savedBlogSubscribers.add(fn);
    return () => savedBlogSubscribers.delete(fn);
  },
};

function useSavedBlogIds() {
  const [ids, setIds] = useState(() => savedBlogsStore.getIds());
  useEffect(
    () =>
      savedBlogsStore.subscribe(() =>
        setIds(new Set(savedBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ------------------------------------------------------------
   LIKED BLOGS STORE (shared + persisted)
   Liking a blog in the article engagement bar drops it into the
   Published → Liked tab (and nowhere else) and survives reloads.
   ------------------------------------------------------------ */
const LIKED_BLOGS_KEY = "ihn-liked-blog-ids";
const loadLikedBlogIds = () => {
  try {
    const raw = localStorage.getItem(LIKED_BLOGS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const likedBlogSubscribers = new Set();
let likedBlogIdsStore = loadLikedBlogIds();
export const likedBlogsStore = {
  getIds: () => likedBlogIdsStore,
  toggle: (id) => {
    const next = new Set(likedBlogIdsStore);
    const wasLiked = next.has(id);
    if (wasLiked) next.delete(id);
    else next.add(id);
    likedBlogIdsStore = next;
    try {
      localStorage.setItem(LIKED_BLOGS_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    likedBlogSubscribers.forEach((fn) => fn());
    return wasLiked;
  },
  subscribe: (fn) => {
    likedBlogSubscribers.add(fn);
    return () => likedBlogSubscribers.delete(fn);
  },
};

function useLikedBlogIds() {
  const [ids, setIds] = useState(() => likedBlogsStore.getIds());
  useEffect(
    () =>
      likedBlogsStore.subscribe(() =>
        setIds(new Set(likedBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ------------------------------------------------------------
   ARCHIVED BLOGS STORE (shared + persisted)
   Archiving an owner article drops it into the Published →
   Archived tab and removes its card from every grid.
   ------------------------------------------------------------ */
const ARCHIVED_BLOGS_KEY = "ihn-archived-blog-ids";
const loadArchivedBlogIds = () => {
  try {
    const raw = localStorage.getItem(ARCHIVED_BLOGS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const archivedBlogSubscribers = new Set();
let archivedBlogIdsStore = loadArchivedBlogIds();
export const archivedBlogsStore = {
  getIds: () => archivedBlogIdsStore,
  has: (id) => archivedBlogIdsStore.has(id),
  toggle: (id) => {
    const next = new Set(archivedBlogIdsStore);
    const wasArchived = next.has(id);
    if (wasArchived) next.delete(id);
    else next.add(id);
    archivedBlogIdsStore = next;
    try {
      localStorage.setItem(ARCHIVED_BLOGS_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    archivedBlogSubscribers.forEach((fn) => fn());
    return wasArchived;
  },
  subscribe: (fn) => {
    archivedBlogSubscribers.add(fn);
    return () => archivedBlogSubscribers.delete(fn);
  },
};

function useArchivedBlogIds() {
  const [ids, setIds] = useState(() => archivedBlogsStore.getIds());
  useEffect(
    () =>
      archivedBlogsStore.subscribe(() =>
        setIds(new Set(archivedBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ------------------------------------------------------------
   DELETED BLOGS STORE (shared + persisted)
   Deleting an owner article removes its card everywhere.
   ------------------------------------------------------------ */
const DELETED_BLOGS_KEY = "ihn-deleted-blog-ids";
const loadDeletedBlogIds = () => {
  try {
    const raw = localStorage.getItem(DELETED_BLOGS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const deletedBlogSubscribers = new Set();
let deletedBlogIdsStore = loadDeletedBlogIds();
export const deletedBlogsStore = {
  getIds: () => deletedBlogIdsStore,
  add: (id) => {
    const next = new Set(deletedBlogIdsStore);
    next.add(id);
    deletedBlogIdsStore = next;
    try {
      localStorage.setItem(DELETED_BLOGS_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    deletedBlogSubscribers.forEach((fn) => fn());
  },
  subscribe: (fn) => {
    deletedBlogSubscribers.add(fn);
    return () => deletedBlogSubscribers.delete(fn);
  },
};

function useDeletedBlogIds() {
  const [ids, setIds] = useState(() => deletedBlogsStore.getIds());
  useEffect(
    () =>
      deletedBlogsStore.subscribe(() =>
        setIds(new Set(deletedBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ------------------------------------------------------------
   HIDDEN BLOGS STORE (shared + persisted)
   "Not interested" on a non-owner article hides its card from
   every blog grid/tab, exactly like Community's hidden posts.
   ------------------------------------------------------------ */
const HIDDEN_BLOGS_KEY = "ihn-hidden-blog-ids";
const loadHiddenBlogIds = () => {
  try {
    const raw = localStorage.getItem(HIDDEN_BLOGS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const hiddenBlogSubscribers = new Set();
let hiddenBlogIdsStore = loadHiddenBlogIds();
export const hiddenBlogsStore = {
  getIds: () => hiddenBlogIdsStore,
  toggle: (id) => {
    const next = new Set(hiddenBlogIdsStore);
    const wasHidden = next.has(id);
    if (wasHidden) next.delete(id);
    else next.add(id);
    hiddenBlogIdsStore = next;
    try {
      localStorage.setItem(HIDDEN_BLOGS_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    hiddenBlogSubscribers.forEach((fn) => fn());
    return wasHidden;
  },
  subscribe: (fn) => {
    hiddenBlogSubscribers.add(fn);
    return () => hiddenBlogSubscribers.delete(fn);
  },
};

function useHiddenBlogIds() {
  const [ids, setIds] = useState(() => hiddenBlogsStore.getIds());
  useEffect(
    () =>
      hiddenBlogsStore.subscribe(() =>
        setIds(new Set(hiddenBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ------------------------------------------------------------
   CREATED BLOGS STORE (shared + persisted)
   Posts written through the "Create Blog" editor are saved here
   so they appear across the category grids, in the Published / My
   tab, and resolve on their own /blog/<slug> page.
   ------------------------------------------------------------ */
const CREATED_BLOGS_KEY = "ihn-created-blogs";
let createdBlogs = loadCreatedBlogs();
const createdBlogSubscribers = new Set();
function loadCreatedBlogs() {
  try {
    const raw = localStorage.getItem(CREATED_BLOGS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
export const createdBlogsStore = {
  getAll: () => createdBlogs,
  add: (blog) => {
    createdBlogs = [blog, ...createdBlogs];
    try {
      localStorage.setItem(CREATED_BLOGS_KEY, JSON.stringify(createdBlogs));
    } catch {
      // ignore persistence failures
    }
    createdBlogSubscribers.forEach((fn) => fn());
  },
  remove: (id) => {
    createdBlogs = createdBlogs.filter((b) => b.id !== id);
    try {
      localStorage.setItem(CREATED_BLOGS_KEY, JSON.stringify(createdBlogs));
    } catch {
      // ignore persistence failures
    }
    createdBlogSubscribers.forEach((fn) => fn());
  },
  update: (id, patch) => {
    createdBlogs = createdBlogs.map((b) => (b.id === id ? { ...b, ...patch, id } : b));
    try {
      localStorage.setItem(CREATED_BLOGS_KEY, JSON.stringify(createdBlogs));
    } catch {
      // ignore persistence failures
    }
    createdBlogSubscribers.forEach((fn) => fn());
  },
  subscribe: (fn) => {
    createdBlogSubscribers.add(fn);
    return () => createdBlogSubscribers.delete(fn);
  },
};

function useCreatedBlogs() {
  const [blogs, setBlogs] = useState(() => createdBlogsStore.getAll());
  useEffect(
    () => createdBlogsStore.subscribe(() => setBlogs(createdBlogsStore.getAll())),
    []
  );
  return blogs;
}

/* ------------------------------------------------------------
   REACTION / COMMENT DISABLED STORES (shared + persisted)
   The create-blog settings can fully turn off likes and comments
   for a published post. When enabled for a blog id, the article's
   like and comment buttons are hidden entirely.
   ------------------------------------------------------------ */
const LIKES_OFF_KEY = "ihn-blog-likes-off";
const COMMENTS_OFF_KEY = "ihn-blog-comments-off";
const loadOffIds = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const likesOffSubscribers = new Set();
const commentsOffSubscribers = new Set();
let likesOffStore = loadOffIds(LIKES_OFF_KEY);
let commentsOffStore = loadOffIds(COMMENTS_OFF_KEY);
export const blogLikesOffStore = {
  getIds: () => likesOffStore,
  toggle: (id) => {
    const next = new Set(likesOffStore);
    const wasOff = next.has(id);
    if (wasOff) next.delete(id);
    else next.add(id);
    likesOffStore = next;
    try {
      localStorage.setItem(LIKES_OFF_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    likesOffSubscribers.forEach((fn) => fn());
    return wasOff;
  },
  subscribe: (fn) => {
    likesOffSubscribers.add(fn);
    return () => likesOffSubscribers.delete(fn);
  },
};
export const blogCommentsOffStore = {
  getIds: () => commentsOffStore,
  toggle: (id) => {
    const next = new Set(commentsOffStore);
    const wasOff = next.has(id);
    if (wasOff) next.delete(id);
    else next.add(id);
    commentsOffStore = next;
    try {
      localStorage.setItem(COMMENTS_OFF_KEY, JSON.stringify([...next]));
    } catch {
      // ignore persistence failures
    }
    commentsOffSubscribers.forEach((fn) => fn());
    return wasOff;
  },
  subscribe: (fn) => {
    commentsOffSubscribers.add(fn);
    return () => commentsOffSubscribers.delete(fn);
  },
};

function useBlogLikesOff() {
  const [ids, setIds] = useState(() => blogLikesOffStore.getIds());
  useEffect(
    () =>
      blogLikesOffStore.subscribe(() => setIds(new Set(blogLikesOffStore.getIds()))),
    []
  );
  return ids;
}

function useBlogCommentsOff() {
  const [ids, setIds] = useState(() => blogCommentsOffStore.getIds());
  useEffect(
    () =>
      blogCommentsOffStore.subscribe(() =>
        setIds(new Set(blogCommentsOffStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ------------------------------------------------------------
   HIDDEN COUNTS STORES (shared + persisted)
   The owner article ⋮ menu can hide/show the like and comment
   counts on the article engagement bar. Performed exactly like
   Community's "Hide likes count" / "Hide comment count".
   ------------------------------------------------------------ */
const HIDDEN_LIKES_KEY = "ihn-hidden-like-count-blog-ids";
const HIDDEN_COMMENTS_KEY = "ihn-hidden-comment-count-blog-ids";
const loadHiddenIds = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const hiddenLikesSubscribers = new Set();
const hiddenCommentsSubscribers = new Set();
let hiddenLikesStore = loadHiddenIds(HIDDEN_LIKES_KEY);
let hiddenCommentsStore = loadHiddenIds(HIDDEN_COMMENTS_KEY);

function makeHiddenToggleStore(subscribers, getStore, setStoreFn, key) {
  return {
    getIds: () => getStore(),
    has: (id) => getStore().has(id),
    toggle: (id) => {
      const next = new Set(getStore());
      const wasHidden = next.has(id);
      if (wasHidden) next.delete(id);
      else next.add(id);
      setStoreFn(next);
      try {
        localStorage.setItem(key, JSON.stringify([...next]));
      } catch {
        // ignore persistence failures
      }
      subscribers.forEach((fn) => fn());
      return wasHidden;
    },
    subscribe: (fn) => {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
  };
}

export const hiddenLikesBlogsStore = makeHiddenToggleStore(
  hiddenLikesSubscribers,
  () => hiddenLikesStore,
  (next) => (hiddenLikesStore = next),
  HIDDEN_LIKES_KEY
);
export const hiddenCommentsBlogsStore = makeHiddenToggleStore(
  hiddenCommentsSubscribers,
  () => hiddenCommentsStore,
  (next) => (hiddenCommentsStore = next),
  HIDDEN_COMMENTS_KEY
);

function useHiddenLikeCounts() {
  const [ids, setIds] = useState(() => hiddenLikesBlogsStore.getIds());
  useEffect(
    () =>
      hiddenLikesBlogsStore.subscribe(() =>
        setIds(new Set(hiddenLikesBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

function useHiddenCommentCounts() {
  const [ids, setIds] = useState(() => hiddenCommentsBlogsStore.getIds());
  useEffect(
    () =>
      hiddenCommentsBlogsStore.subscribe(() =>
        setIds(new Set(hiddenCommentsBlogsStore.getIds()))
      ),
    []
  );
  return ids;
}

/* ============================================================
   IHN — BLOGS PAGE (extended)
   Single-file, frontend-only, production-quality editorial Blogs
   experience for the IHN website:
     - Blogs listing + hero + editorial sections (/blogs)
     - Category listing (/blogs/:category)
     - Individual blog article (/blog/:slug)
   All listing/editorial styles use the "ihnBlog-"/"ihn-category-"
   prefixes; article styles use the "ihn-article-" prefix.
   ============================================================ */

/* ------------------------------------------------------------
   1. PURE DATA HELPERS
   ------------------------------------------------------------ */

function getTrending(blogs) {
  return blogs.filter((b) => b.isTrending === true).slice(0, 5);
}

function getMostLiked(blogs) {
  return [...blogs].sort((a, b) => b.likes - a.likes).slice(0, 6);
}

function getEditorsPicks(blogs) {
  return blogs.filter((b) => b.isEditorsPickBadge === true).slice(0, 5);
}

function getExpertVoices(blogs) {
  return blogs.filter((b) => b.isExpertVoice === true).slice(0, 4);
}

export function formatInitials(name) {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function formatDateDDMMYYYY(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

// Formats a date string into the required dd/Month-yyyy
// Works with ISO dates ("2026-09-09") and human-readable dates ("January 3, 2026").
function formatArticleDate(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const day = String(parsed.getDate());
  const month = months[parsed.getMonth()];
  const year = parsed.getFullYear();
  return `${day} ${month}, ${year}`;
}

function buildShareUrl(platform, { title, url }) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  switch (platform) {
    case "gmail":
      return `https://mail.google.com/mail/?view=cm&fs=1&su=${encodedTitle}&body=${encodedUrl}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case "whatsapp":
      return `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`;
    default:
      return null;
  }
}

/* ------------------------------------------------------------
   2. PARALLAX HOOK
   ------------------------------------------------------------ */

export function useParallax(ref, speed = 0.12) {
  useEffect(() => {
    if (!ref.current) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return undefined;

    let rafId = null;

    const update = () => {
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const distanceFromCenter = rect.top + rect.height / 2 - viewportCenter;
      const offset = distanceFromCenter * speed * -0.1;
      node.style.transform = `translate3d(0, ${offset}px, 0) scale(1.06)`;
    };

    const tick = () => {
      update();
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [ref, speed]);
}

/* ------------------------------------------------------------
   3. SMALL PRESENTATIONAL COMPONENTS
   ------------------------------------------------------------ */

function EmptyState({ title, message }) {
  return (
    <div className="ihnBlog-empty">
      <p className="ihnBlog-empty__title">{title}</p>
      <p className="ihnBlog-empty__message">{message}</p>
    </div>
  );
}

export function BlogNavigation({ activeCategory }) {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const [indicator, setIndicator] = useState(null);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    const measure = () => {
      const track = trackRef.current;
      const activeBtn = track?.querySelector(".ihnBlog-nav__item--active");
      if (track && activeBtn) {
        const trackRect = track.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();
        setIndicator({
          left: btnRect.left - trackRect.left + track.scrollLeft,
          width: btnRect.width,
        });
      } else {
        setIndicator(null);
      }
    };
    measure();
    const activeBtn = trackRef.current?.querySelector(".ihnBlog-nav__item--active");
    activeBtn?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    const scheduleMeasure = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", scheduleMeasure);
    track?.addEventListener("scroll", scheduleMeasure, { passive: true });
    const ro = track && typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleMeasure)
      : null;
    ro?.observe(track);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      track?.removeEventListener("scroll", scheduleMeasure);
    };
  }, [activeCategory]);

  useEffect(() => {
    if (!direction) return;
    const step = () => {
      const track = trackRef.current;
      if (!track) return;
      track.scrollLeft += direction * 4;
      if (track.scrollLeft <= 0) {
        track.scrollLeft = 0;
        setDirection(0);
        return;
      }
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 1) {
        setDirection(0);
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [direction]);

  const handleTrackMouseMove = (e) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const edge = 70;
    const canLeft = track.scrollLeft > 0;
    const canRight = track.scrollLeft + track.clientWidth < track.scrollWidth - 1;
    let dir = 0;
    if (e.clientX < rect.left + edge && canLeft) dir = -1;
    else if (e.clientX > rect.right - edge && canRight) dir = 1;
    setDirection(dir);
  };

  return (
    <nav className="ihnBlog-nav" aria-label="Blog categories">
      <div
        className="ihnBlog-nav__track"
        ref={trackRef}
        onMouseMove={handleTrackMouseMove}
        onMouseLeave={() => setDirection(0)}
      >
        {CATEGORIES.map((cat) => {
          const isActive = cat === activeCategory;
          return (
            <button
              key={cat}
              type="button"
              className={
                "ihnBlog-nav__item" +
                (isActive ? " ihnBlog-nav__item--active" : "")
              }
              aria-current={isActive ? "true" : undefined}
              onClick={() =>
                navigate(
                  cat === "Blogs"
                    ? "/blogs"
                    : `/blogs/${encodeURIComponent(cat)}`
                )
              }
            >
              {cat}
            </button>
          );
        })}
        <span
          className="ihnBlog-nav__indicator"
          aria-hidden="true"
          style={indicator ? { transform: `translateX(${indicator.left}px)`, width: indicator.width } : { opacity: 0 }}
        />
      </div>
    </nav>
  );
}

function HeroSection() {
  const heroRef = useRef(null);
  const navigate = useNavigate();
  useParallax(heroRef, 0.18);

  return (
    <header className="ihnBlog-hero">
      <div className="ihnBlog-hero__text">
        <h1 className="ihnBlog-h1">Stories, Ideas &amp; Experiences</h1>
        <p className="ihnBlog-hero__desc">
          IHN Blogs gathers stories and insight from across hospitality,
          travel, food, business, lifestyle, fashion, events, technology and
          general experiences — reported and written by the IHN editorial
          team and contributors we trust.
        </p>
        <button
          type="button"
          className="ihnBlog-btn ihnBlog-btn--primary"
          onClick={() => navigate("/blog/create")}
        >
          Create Blog
        </button>
      </div>
      <div className="ihnBlog-hero__visual">
        <div className="ihnBlog-hero__visualClip">
          <img
            ref={heroRef}
            className="ihnBlog-hero__image"
            src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1400&q=80"
            alt="A sunlit hotel terrace overlooking a quiet coastline"
            loading="lazy"
          />
        </div>
      </div>
    </header>
  );
}

function TrendingItem({ index, blog, onOpen }) {
  return (
    <li className="ihnBlog-trending__item">
      <button
        type="button"
        className="ihnBlog-trending__link"
        onClick={() => onOpen(blog)}
      >
        <span className="ihnBlog-trending__index">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="ihnBlog-trending__title">{blog.title}</span>
      </button>
    </li>
  );
}

function FeaturedEditorial({ blog, onOpen, isSaved, onToggleSave }) {
  const featuredRef = useRef(null);
  useParallax(featuredRef, 0.1);

  if (!blog) return null;

  return (
    <div className="ihnBlog-featured">
      <button
        type="button"
        className="ihnBlog-featured__frame"
        onClick={() => onOpen(blog)}
        aria-label={`Read ${blog.title}`}
      >
        <span className="ihnBlog-featured__zoom">
          <img
            ref={featuredRef}
            className="ihnBlog-featured__image"
            src={blogCoverImage(blog)}
            alt={blog.title}
            loading="lazy"
          />
        </span>
        <span className="ihnBlog-featured__overlay" aria-hidden="true">
          <span className="ihnBlog-card__overlayMeta">
            <span className="ihnBlog-card__overlayId">
              <span className="ihnBlog-card__overlayAvatar">
                {formatInitials(blog.author)}
              </span>
              <span className="ihnBlog-card__overlayNameGroup">
                <span className="ihnBlog-card__overlayName">{blog.author}</span>
                {blog.company && (
                  <span className="ihnBlog-card__overlayCompany">
                    {blog.company}
                  </span>
                )}
              </span>
            </span>
            <span className="ihnBlog-card__overlayDate">
              {formatDateDDMMYYYY(blog.date)}
            </span>
          </span>
        </span>
      </button>
      <BlogSaveButton blog={blog} isSaved={isSaved} onToggleSave={onToggleSave} />
      <p className="ihnBlog-featured__category">{blog.category}</p>
      <h3 className="ihnBlog-featured__title">{blog.title}</h3>
    </div>
  );
}

function MostLiked({ blogs, onOpen, savedBlogIds, onToggleSave }) {
  const mostLiked = useMemo(() => getMostLiked(blogs), [blogs]);
  const [selectedId, setSelectedId] = useState(mostLiked[0]?.id);
  const featured =
    mostLiked.find((b) => b.id === selectedId) || mostLiked[0];

  return (
    <section className="ihnBlog-section ihnBlog-twoCol" aria-label="Most liked stories">
      <div className="ihnBlog-twoCol__left">
        <h2 className="ihnBlog-h2">Most Liked</h2>
        <ul className="ihnBlog-commented__list">
          {mostLiked.map((blog) => (
            <li key={blog.id} className="ihnBlog-commented__item">
              <button
                type="button"
                className={
                  "ihnBlog-commented__link" +
                  (blog.id === selectedId ? " ihnBlog-commented__link--active" : "")
                }
                onClick={() => setSelectedId(blog.id)}
              >
                <span className="ihnBlog-commented__category">{blog.category}</span>
                <span className="ihnBlog-commented__title">{blog.title}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="ihnBlog-twoCol__right">
        <FeaturedEditorial
          blog={featured}
          onOpen={onOpen}
          isSaved={savedBlogIds.has(featured.id)}
          onToggleSave={onToggleSave}
        />
      </div>
    </section>
  );
}

function EditorsPicks({ blogs, onOpen }) {
  const picks = useMemo(() => getEditorsPicks(blogs), [blogs]);

  return (
    <section className="ihnBlog-section ihnBlog-picks-section" aria-label="Editor's picks">
      <h2 className="ihnBlog-h2 ihnBlog-h2--picks">Editor&rsquo;s Picks</h2>
      <ul className="ihnBlog-picks__list">
        {picks.map((blog) => (
          <PicksItem key={blog.id} blog={blog} onOpen={onOpen} />
        ))}
      </ul>
    </section>
  );
}

function PicksItem({ blog, onOpen }) {
  const imgRef = useRef(null);
  useParallax(imgRef, 0.16);

  return (
    <li className="ihnBlog-picks__item">
      <button
        type="button"
        className="ihnBlog-picks__link"
        onClick={() => onOpen(blog)}
      >
        <span className="ihnBlog-picks__circle">
          <img
            ref={imgRef}
            src={blogCoverImage(blog)}
            alt={blog.title}
            loading="lazy"
            className="ihnBlog-picks__image"
          />
        </span>
        <span className="ihnBlog-picks__category">{blog.category}</span>
      </button>
    </li>
  );
}

function BlogCard({ blog, onOpen, isSaved, onToggleSave }) {
  const navigate = useNavigate();

  const commentSeeds = useMemo(() => buildArticleCommentSeeds(blog), [blog]);
  const entityId = useMemo(() => articleCommentEntityId(blog), [blog]);
  const liveCommentCount = useCommentCount(commentSeeds, entityId);

  return (
    <article className="ihnBlog-card">
      <div className="ihnBlog-card__media">
        <button
          type="button"
          className="ihnBlog-card__frame"
          onClick={() => onOpen(blog)}
          aria-label={`Read ${blog.title}`}
        >
          <img
            className="ihnBlog-card__image"
            src={blogCoverImage(blog)}
            alt={blog.title}
            loading="lazy"
          />
          <span className="ihnBlog-card__overlay" aria-hidden="true">
            <span className="ihnBlog-card__overlayMeta">
              <span className="ihnBlog-card__overlayId">
                <span className="ihnBlog-card__overlayAvatar">
                  {formatInitials(blog.author)}
                </span>
                <span className="ihnBlog-card__overlayNameGroup">
                  <span className="ihnBlog-card__overlayName">{blog.author}</span>
                  {blog.company && (
                    <span className="ihnBlog-card__overlayCompany">
                      {blog.company}
                    </span>
                  )}
                </span>
              </span>
              <span className="ihnBlog-card__overlayDate">
                {formatDateDDMMYYYY(blog.date)}
              </span>
            </span>
          </span>
        </button>
        <BlogSaveButton blog={blog} isSaved={isSaved} onToggleSave={onToggleSave} />
      </div>

      <p className="ihnBlog-card__category">{blog.category}</p>
      <h3 className="ihnBlog-card__title">
        <button type="button" className="ihnBlog-card__titleBtn" onClick={() => onOpen(blog)}>
          {blog.title}
        </button>
      </h3>
      <p className="ihnBlog-card__subhead">{blog.subhead || blog.excerpt}</p>

      <div className="ihnBlog-card__actions">
        <button
          type="button"
          className="ihnBlog-card__actionBtn"
          onClick={() => navigate(`${blogArticleHref(blog)}#comments`)}
        >
          <span className="ihnBlog-card__actionCount">{liveCommentCount}</span> Comment
        </button>
      </div>
    </article>
  );
}

function BlogGrid({ blogs, onOpen, activeCategory, initialCount, savedBlogIds, onToggleSave }) {
  if (blogs.length === 0) {
    return (
      <EmptyState
        title="No blogs found"
        message={`There are currently no blog posts available in ${
          activeCategory === "Blogs" ? "this section" : activeCategory
        }.`}
      />
    );
  }

  return (
    <BlogGridWithMore
      blogs={blogs}
      onOpen={onOpen}
      initialCount={initialCount}
      savedBlogIds={savedBlogIds}
      onToggleSave={onToggleSave}
    />
  );
}

function BlogGridWithMore({ blogs, onOpen, initialCount: fixedCount, savedBlogIds, onToggleSave }) {
  const width = useViewportWidth();
  const [showAll, setShowAll] = useState(false);

  const defaultInitialCount = width < 640 ? 3 : width < 1024 ? 6 : 9;
  const initialCount = fixedCount ?? defaultInitialCount;
  const visibleBlogs = showAll ? blogs : blogs.slice(0, initialCount);
  const canToggle = blogs.length > initialCount;

  if (blogs.length === 0) {
    return <EmptyState title="No blogs found" message="No blog posts available." />;
  }

  return (
    <div className="ihnBlog-gridWrap">
      <div className="ihnBlog-grid">
        {visibleBlogs.map((blog) => (
          <BlogCard
            key={blog.id}
            blog={blog}
            onOpen={onOpen}
            isSaved={savedBlogIds.has(blog.id)}
            onToggleSave={onToggleSave}
          />
        ))}
      </div>

      {canToggle && (
        <div className="ihnBlog-gridToggle">
          <button
            type="button"
            className={"ihnBlog-btn" + (showAll ? " ihnBlog-btn--secondary" : " ihnBlog-btn--primary")}
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "View Less" : "View More"}
          </button>
        </div>
      )}
    </div>
  );
}

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );
  useEffect(() => {
    let frame;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(window.innerWidth));
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(frame);
    };
  }, []);
  return width;
}

function Archives({ blogs, onOpen, savedBlogIds, onToggleSave }) {
  const archives = useMemo(() => getExpertVoices(blogs), [blogs]);
  const trending = useMemo(() => getTrending(blogs), [blogs]);
  const refs = [useRef(null), useRef(null), useRef(null)];
  useParallax(refs[0], 0.08);
  useParallax(refs[1], 0.08);
  useParallax(refs[2], 0.08);

  return (
    <section className="ihnBlog-section" aria-label="Stories worth sharing">
      <h2 className="ihnBlog-h2 ihnBlog-section__title">Stories Worth Sharing</h2>
      <div className="ihnBlog-twoCol">
        <div className="ihnBlog-twoCol__left">
          <h3 className="ihnBlog-h3">Expert Voices</h3>
        <div className="ihnBlog-expert__grid">
          {archives.map((blog, index) => (
            <div className="ihnBlog-expert__cell" key={blog.id}>
              <button
                type="button"
                className="ihnBlog-expert__card"
                onClick={() => onOpen(blog)}
              >
                <span className="ihnBlog-expert__frame">
                  <img
                    ref={refs[index]}
                    className="ihnBlog-expert__image"
                    src={blogCoverImage(blog)}
                    alt={blog.title}
                    loading="lazy"
                  />
                  <span className="ihnBlog-expert__overlay" aria-hidden="true">
                    <span className="ihnBlog-expert__overlayMeta">
                      <span className="ihnBlog-card__overlayAvatar">
                        {formatInitials(blog.author)}
                      </span>
                      <span className="ihnBlog-card__overlayNameGroup">
                        <span className="ihnBlog-card__overlayName">{blog.author}</span>
                        {blog.company && (
                          <span className="ihnBlog-card__overlayCompany">
                            {blog.company}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="ihnBlog-expert__overlayDate">
                      {formatDateDDMMYYYY(blog.date)}
                    </span>
                  </span>
                </span>
                <span className="ihnBlog-expert__category">{blog.category}</span>
                <span className="ihnBlog-expert__title">{blog.title}</span>
              </button>
              <BlogSaveButton
                blog={blog}
                isSaved={savedBlogIds.has(blog.id)}
                onToggleSave={onToggleSave}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="ihnBlog-twoCol__right">
        <h3 className="ihnBlog-h3">Trending</h3>
        <ol className="ihnBlog-trending__list">
          {trending.map((blog, index) => (
            <TrendingItem key={blog.id} index={index} blog={blog} onOpen={onOpen} />
          ))}
        </ol>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------
   4. MAIN PAGE COMPONENT
   ------------------------------------------------------------ */

export default function BlogsPage() {
  const navigate = useNavigate();
  const archivedBlogIds = useArchivedBlogIds();
  const deletedBlogIds = useDeletedBlogIds();
  const hiddenBlogIds = useHiddenBlogIds();
  const createdBlogs = useCreatedBlogs();
  const hiddenIds = useMemo(
    () => new Set([...archivedBlogIds, ...deletedBlogIds, ...hiddenBlogIds]),
    [archivedBlogIds, deletedBlogIds, hiddenBlogIds]
  );
  const visibleBLOGS = useMemo(
    () => [...createdBlogs, ...BLOGS].filter((b) => !hiddenIds.has(b.id)),
    [hiddenIds, createdBlogs]
  );
  const topCommented = useMemo(
    () => [...visibleBLOGS].sort((a, b) => b.comments - a.comments).slice(0, 12),
    [visibleBLOGS]
  );
  const savedBlogIds = useSavedBlogIds();
  const [toast, setToast] = useState(null);
  const toastIdRef = useRef(0);

  const showToast = useCallback((message) => {
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, message });
  }, []);

  const handleToggleSave = useCallback(
    (blog) => {
      const wasSaved = savedBlogsStore.toggle(blog.id);
      showToast(wasSaved ? "Blog removed from saved" : "Blog saved");
    },
    [showToast]
  );

  const handleOpenBlog = (blog) => {
    navigate(blogArticleHref(blog));
  };

  return (
    <div className="ihnBlog-page">
      <div className="ihnBlog-nav-search-wrap">
        <BlogNavigation activeCategory="Blogs" />
      </div>

      <HeroSection />

      <Archives
        blogs={visibleBLOGS}
        onOpen={handleOpenBlog}
        savedBlogIds={savedBlogIds}
        onToggleSave={handleToggleSave}
      />

      <EditorsPicks blogs={visibleBLOGS} onOpen={handleOpenBlog} />

      <section className="ihnBlog-section" aria-label="All blog posts">
        <h2 className="ihnBlog-h2">Blogs</h2>
        <BlogGrid
          blogs={topCommented}
          onOpen={handleOpenBlog}
          activeCategory="Blogs"
          savedBlogIds={savedBlogIds}
          onToggleSave={handleToggleSave}
        />
      </section>

      <MostLiked
        blogs={visibleBLOGS}
        onOpen={handleOpenBlog}
        savedBlogIds={savedBlogIds}
        onToggleSave={handleToggleSave}
      />

      <BlogsBottomNav
        activeTab={null}
        onNavigate={(key) => {
          if (key === "create") navigate("/blog/create");
          else navigate("/blogs/Published", { state: { postsTab: key } });
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
      {toast && (
        <BlogToast
          key={toast.id}
          message={toast.message}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------
   5. CATEGORY PAGE (extended part of the Blogs page)
   Rendered at /blogs/:category as a separate full page.
   ------------------------------------------------------------ */

const BlogToast = ({ message, onDismiss }) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="ihnBlog-toast" role="status" aria-live="polite">
      <span className="ihnBlog-toast-message">{message}</span>
      <button
        type="button"
        className="ihnBlog-toast-close"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
};

const BlogSaveButton = ({ blog, isSaved, onToggleSave }) => (
  <button
    type="button"
    className={"ihn-blog-save" + (isSaved ? " ihn-blog-save--active" : "")}
    onClick={() => onToggleSave(blog)}
    aria-label={isSaved ? `Remove ${blog.title} from saved` : `Save ${blog.title}`}
    aria-pressed={isSaved}
    title={isSaved ? "Remove from saved" : "Save this blog"}
  >
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
    </svg>
  </button>
);

function CategoryCard({ blog, onOpen, clickable = true, isSaved, onToggleSave, showCategory = false }) {
  const navigate = useNavigate();
  const imageRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(() => blogCoverImage(blog));
  useParallax(imageRef, 0.4);

  const commentSeeds = useMemo(() => buildArticleCommentSeeds(blog), [blog]);
  const entityId = useMemo(() => articleCommentEntityId(blog), [blog]);
  const liveCommentCount = useCommentCount(commentSeeds, entityId);

  const ImageArea = clickable ? "button" : "div";

  return (
    <article
      className={
        "ihn-category-card" +
        (clickable ? "" : " ihn-category-card--pending")
      }
    >
      <ImageArea
        type={clickable ? "button" : undefined}
        className="ihn-category-image-wrap"
        onClick={clickable ? () => onOpen(blog) : undefined}
        aria-label={clickable ? `Read ${blog.title}` : undefined}
        tabIndex={clickable ? 0 : -1}
      >
        <span className="ihn-category-image-zoom" ref={imageRef}>
          <img
            className="ihn-category-image"
            src={imageSrc}
            alt={blog.title}
            loading="lazy"
            onError={() => {
              if (imageSrc !== CREATED_BLOG_DEFAULT_COVER) {
                setImageSrc(CREATED_BLOG_DEFAULT_COVER);
              }
            }}
          />
        </span>
        <span className="ihnBlog-card__overlay" aria-hidden="true">
          <span className="ihnBlog-card__overlayMeta">
            <span className="ihnBlog-card__overlayId">
              <span className="ihnBlog-card__overlayAvatar">
                {formatInitials(blog.author)}
              </span>
              <span className="ihnBlog-card__overlayNameGroup">
                <span className="ihnBlog-card__overlayName">{blog.author}</span>
                {blog.company && (
                  <span className="ihnBlog-card__overlayCompany">
                    {blog.company}
                  </span>
                )}
              </span>
            </span>
            <span className="ihnBlog-card__overlayDate">
              {formatDateDDMMYYYY(blog.date)}
            </span>
          </span>
        </span>
      </ImageArea>
      <BlogSaveButton blog={blog} isSaved={isSaved} onToggleSave={onToggleSave} />
      <div className="ihn-category-content">
        {showCategory && (
          <div className="ihnBlog-card__category">{blog.category}</div>
        )}
        <h3 className="ihnBlog-card__title">
          {clickable ? (
            <button
              type="button"
              className="ihnBlog-card__titleBtn"
              onClick={() => onOpen(blog)}
            >
              {blog.title}
            </button>
          ) : (
            <span className="ihnBlog-card__titleBtn ihn-category-title--pending">
              {blog.title}
            </span>
          )}
        </h3>
        <p className="ihnBlog-card__subhead">{blog.subhead || blog.excerpt}</p>
        <div className="ihnBlog-card__actions">
          <button
            type="button"
            className="ihnBlog-card__actionBtn"
            onClick={() => navigate(`${blogArticleHref(blog)}#comments`)}
          >
            <span className="ihnBlog-card__actionCount">{liveCommentCount}</span>{" "}
            Comment
          </button>
        </div>
      </div>
    </article>
  );
}

function parseBlogDate(dateStr) {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

const CATEGORY_EXCLUDED_TITLES = new Set([
  "The Fermented Foods Coming Back to Indian Kitchens",
  "Where to Watch the Monsoon Roll In",
  "The Temple Towns Worth a Detour on Any South India Route",
  "Chasing the First Light in the Aravalli Hills",
  "How Trust Became India's Most Underrated Business Asset",
  "The Unwritten Rules of Hospitality Negotiation",
  "What Opening a Second Location Teaches a Family Business",
  "Building Evenings That Don't End With a Screen",
  "The Styling Rule That Saves Every Indian Wardrobe",
  "The Street Style of Mumbai's Train Ladies",
  "When Denim Replaced the Formal Jacket in Indian Offices",
  "The Small Apps Quietly Running India's Best Hotels",
  "Why Offline-First Software Is Winning in Tier-2 India",
  "The Sensor Tricks That Cut Hotel Energy Bills in Half",
  "On the Small Joys You Don't Plan For",
  "A Few Things We're Thinking About Right Now",
  "What We Mean When We Say 'Back to Basics'",
]);

const BlogsBottomNav = ({ activeTab, onNavigate }) => {
  const items = [
    {
      key: "posts",
      label: "My Posts",
      icon: <span role="img" aria-label="My Posts" style={{ fontSize: "26px" }}>🗂️</span>,
    },
    {
      key: "liked",
      label: "Liked",
      icon: <span role="img" aria-label="Liked" style={{ fontSize: "26px" }}>❤️</span>,
    },
    {
      key: "create",
      label: "Create",
      icon: <span role="img" aria-label="Create" style={{ fontSize: "26px" }}>➕</span>,
    },
    {
      key: "saved",
      label: "Saved",
      icon: <span role="img" aria-label="Saved" style={{ fontSize: "26px" }}>⭐</span>,
    },
    {
      key: "archive",
      label: "Archive",
      icon: <span role="img" aria-label="Archive" style={{ fontSize: "26px" }}>📦</span>,
    },
  ];

  return (
    <nav className="Community-bottom-nav" aria-label="Blog shortcuts">
      {items.map((item) => (
        <button
          type="button"
          key={item.key}
          className={`Community-bottom-nav-btn ${
            activeTab === item.key ? "Community-bottom-nav-btn--active" : ""
          }`}
          onClick={() => onNavigate(item.key)}
          aria-label={item.label}
        >
          {item.icon}
          <span className="Community-bottom-nav-label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
};

export function BlogsCategoryPage() {
  const { category = "Blogs" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCategory = CATEGORIES.includes(category) ? category : "Blogs";
  const width = useViewportWidth();
  const [showAll, setShowAll] = useState(false);
  const [postsTab, setPostsTab] = useState(
    () => location.state?.postsTab || "posts"
  );
  const savedBlogIds = useSavedBlogIds();
  const likedBlogIds = useLikedBlogIds();
  const archivedBlogIds = useArchivedBlogIds();
  const deletedBlogIds = useDeletedBlogIds();
  const hiddenBlogIds = useHiddenBlogIds();
  const createdBlogs = useCreatedBlogs();
  const [toast, setToast] = useState(null);
  const toastIdRef = useRef(0);

  const isMyPosts = selectedCategory === "Published";

  const findBlog = useCallback(
    (id) => {
      const inCreated = createdBlogs.find((b) => b.id === id);
      return inCreated || BLOGS.find((b) => b.id === id);
    },
    [createdBlogs]
  );

  const hiddenIds = useMemo(
    () =>
      new Set([...archivedBlogIds, ...deletedBlogIds, ...hiddenBlogIds]),
    [archivedBlogIds, deletedBlogIds, hiddenBlogIds]
  );

  const ownerPublishedBlogs = useMemo(
    () => [
      ...createdBlogs,
      ...BLOGS.filter(
        (b) => b.author === "Rahul Mehta" && b.company === "ABC Hospitality"
      ),
    ],
    [createdBlogs]
  );

  const filteredBlogs = useMemo(() => {
    let result;
    if (selectedCategory === "Published") {
      if (postsTab === "archive")
        result = ownerPublishedBlogs.filter(
          (b) => archivedBlogIds.has(b.id) && !deletedBlogIds.has(b.id)
        );
      else if (postsTab === "saved")
        result = [...savedBlogIds]
          .map(findBlog)
          .filter((b) => b && !hiddenIds.has(b.id));
      else if (postsTab === "liked")
        result = [...likedBlogIds]
          .map(findBlog)
          .filter((b) => b && !hiddenIds.has(b.id));
      else result = ownerPublishedBlogs.filter((b) => !hiddenIds.has(b.id));
    } else if (selectedCategory === "Blogs") {
      result = [...createdBlogs, ...BLOGS].filter((b) => !hiddenIds.has(b.id));
    } else {
      result = [
        ...createdBlogs.filter(
          (b) => b.category === selectedCategory && !hiddenIds.has(b.id)
        ),
        ...BLOGS.filter(
          (b) =>
            b.category === selectedCategory &&
            !hiddenIds.has(b.id) &&
            !CATEGORY_EXCLUDED_TITLES.has(b.title)
        ),
      ];
    }
    return result.sort(
      (a, b) => parseBlogDate(b.date) - parseBlogDate(a.date)
    );
  }, [selectedCategory, postsTab, savedBlogIds, likedBlogIds, findBlog, ownerPublishedBlogs, archivedBlogIds, deletedBlogIds, hiddenIds, createdBlogs]);

  useEffect(() => {
    setShowAll(false);
  }, [selectedCategory, postsTab]);

  useEffect(() => {
    const tab = location.state?.postsTab;
    if (tab) setPostsTab(tab);
  }, [location.state?.postsTab]);

  const defaultInitialCount = width < 640 ? 3 : width < 1024 ? 6 : 9;
  const visibleBlogs = showAll
    ? filteredBlogs
    : filteredBlogs.slice(0, defaultInitialCount);
  const canToggle = filteredBlogs.length > defaultInitialCount;

  const showToast = useCallback((message) => {
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, message });
  }, []);

  const handleToggleSave = useCallback(
    (blog) => {
      const wasSaved = savedBlogsStore.toggle(blog.id);
      showToast(wasSaved ? "Blog removed from saved" : "Blog saved");
    },
    [showToast]
  );

  const handleOpenBlog = (blog) => {
    navigate(blogArticleHref(blog));
  };

  const postsHeading =
    postsTab === "posts"
      ? "My Posts"
      : postsTab === "archive"
        ? "Archive Photos"
        : postsTab === "saved"
          ? "Saved Posts"
          : postsTab === "liked"
            ? "Liked Posts"
            : "My Posts";

  const emptyStateCopy =
    postsTab === "posts"
      ? "You have not posted anything yet."
      : postsTab === "archive"
        ? "Archived posts will appear here."
        : postsTab === "saved"
          ? "Posts you save will appear here."
          : postsTab === "liked"
            ? "Posts you react to will appear here."
            : "You have not posted anything yet.";

  const gridContent =
    visibleBlogs.length > 0 ? (
      <>
        <div className="ihn-category-grid">
          {visibleBlogs.map((blog) => (
            <CategoryCard
              key={blog.id}
              blog={blog}
              onOpen={handleOpenBlog}
              clickable
              isSaved={savedBlogIds.has(blog.id)}
              onToggleSave={handleToggleSave}
              showCategory={isMyPosts}
            />
          ))}
        </div>

        {canToggle && (
          <div className="ihnBlog-gridToggle">
            <button
              type="button"
              className={
                "ihnBlog-btn" +
                (showAll ? " ihnBlog-btn--secondary" : " ihnBlog-btn--primary")
              }
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "View Less" : "View More"}
            </button>
          </div>
        )}
      </>
    ) : (
      <div className="ihn-category-empty">
        <h3>{isMyPosts ? "No Posts Found" : "No blogs found"}</h3>
        <p>
          {isMyPosts
            ? emptyStateCopy
            : "There are currently no blog posts available in this category."}
        </p>
      </div>
    );

  return (
    <div className="ihnBlog-page">
      <div className="ihnBlog-nav-search-wrap">
        <BlogNavigation activeCategory={selectedCategory} />
      </div>

      <section className="ihn-category-page" aria-label={selectedCategory}>
        <div className="ihn-category-inner">
          {isMyPosts && (
            <>
              <div className="Community-myposts-backbar">
                <div className="Community-myposts-tabs" role="tablist" aria-label="My posts">
                  {[
                    ["posts", "My"],
                    ["archive", "Archived"],
                    ["saved", "Saved"],
                    ["liked", "Liked"],
                    ["create", "Create"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={value === "create" ? false : postsTab === value}
                      className={
                        "Community-myposts-tab" +
                        (value === "create" ? " Community-myposts-tab-create" : "") +
                        (value !== "create" && postsTab === value
                          ? " Community-myposts-tab-active"
                          : "")
                      }
                      onClick={() =>
                        value === "create"
                          ? navigate("/blog/create")
                          : setPostsTab(value)
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <header className="Community-page-header">
                <div className="Community-center-heading-wrap Community-myposts-heading">
                  <div className="Community-myposts-title-row">
                    <h1 className="Community-heading-main">{postsHeading}</h1>
                  </div>
                </div>
              </header>
            </>
          )}

          {gridContent}
        </div>
      </section>
      <BlogsBottomNav
        activeTab={isMyPosts ? postsTab : null}
        onNavigate={(key) => {
          if (key === "create") navigate("/blog/create");
          else navigate("/blogs/Published", { state: { postsTab: key } });
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
      {toast && (
        <BlogToast
          key={toast.id}
          message={toast.message}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}

/* ============================================================
   5b. BLOG CREATE PAGE
   Professional editorial composer. Publishing a new post writes it
   straight into the persisted created-blogs store so it immediately
   appears in the Published / My tab, the matching category grid and
   resolves on its own /blog/<slug> page.
   ============================================================ */

const CREATE_CATEGORY_OPTIONS = CATEGORIES.filter(
  (c) => c !== "Published" && c !== "Blogs"
);

const CREATED_BLOG_DEFAULT_COVER =
  "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1400&q=80";

const blogCoverImage = (blog) => {
  if (blog && blog.coverPhoto) return blog.coverPhoto;
  if (blog && blog.mediaKinds && blog.mediaKinds[0] === "video") {
    const thumb = blog.videoThumbnails && blog.videoThumbnails.find((t) => t);
    if (thumb) return thumb;
  }
  return (blog && blog.images && blog.images[0]) || CREATED_BLOG_DEFAULT_COVER;
};

const isCreatedBlog = (blog) => String(blog && blog.id).startsWith("created-");

const blogArticleHref = (blog) =>
  isCreatedBlog(blog)
    ? `/blog/${blog.slug || titleToSlug(blog.title)}`
    : `/blog/${titleToSlug(blog.title)}`;

// Converts a resolved article back into the create-page "cards" shape so the
// editor can open pre-filled with the existing media, headings and body text.
function articleToCreateCards(article) {
  const media = (article.images || []).map((src, i) => ({
    src,
    kind: (article.mediaKinds && article.mediaKinds[i]) || "image",
    thumbnail: (article.videoThumbnails && article.videoThumbnails[i]) || "",
    edits: (article.videoEdits && article.videoEdits[i]) || null,
    aspect: (article.mediaAspects && article.mediaAspects[i]) || null,
  }));
  const blocks = buildArticleBlocks(article) || [];
  const cards = [];
  const firstMediaIsInline = blocks.some(
    (b) =>
      (b.type === "image" || b.type === "video") &&
      (b.imageIndex == null || b.imageIndex === 0)
  );
  // Only seed the first card with media when the article was actually published
  // with a lead photo. Created blogs whose first card had no photo keep a
  // text-only first card instead of inheriting the default cover image.
  const canPrefillLead = article.hasLeadMedia !== false && !firstMediaIsInline;
  const lead = canPrefillLead ? (media[0] || {}) : {};
  let active = {
    heading: "",
    text: "",
    image: lead.src || "",
    kind: lead.kind || "image",
    thumbnail: lead.thumbnail || "",
    mediaAspect: lead.aspect || null,
    videoEdits: lead.edits || null,
    youtube: "",
  };

  const attachText = (text, isHeading) => {
    if (!active) {
      active = { heading: "", text: "", image: "", kind: "image", youtube: "" };
    }
    if (isHeading) {
      active.heading = active.heading ? `${active.heading} ${text}` : text;
    } else {
      active.text = active.text ? `${active.text}\n\n${text}` : text;
    }
  };

  blocks.forEach((block) => {
    if (block.type === "image" || block.type === "video") {
      const mi = block.imageIndex != null ? block.imageIndex : 0;
      const m = media[mi] || {};
      if (active) cards.push(active);
      active = {
        heading: "",
        text: "",
        image: m.src || "",
        kind: m.kind || "image",
        thumbnail: m.thumbnail || "",
        mediaAspect: m.aspect || null,
        videoEdits: m.edits || null,
        youtube: "",
      };
    } else if (block.type === "youtube") {
      if (active && active.image) {
        cards.push(active);
        active = { heading: "", text: "", image: "", kind: "image", youtube: "" };
      }
      active.youtube = String(block.src || "");
    } else if (block.type === "heading") {
      attachText(block.text, true);
    } else {
      attachText(block.text || "", false);
    }
  });
  if (active) cards.push(active);
  return cards
    .filter((c) => c.image.trim() || c.heading.trim() || c.text.trim() || c.youtube.trim())
    .slice(0, 5);
}

function BlogCreateToggle({ label, description, checked, onChange, disabled }) {
  return (
    <div className="ihn-create-toggle">
      <div className="ihn-create-toggle-copy">
        <span className="ihn-create-toggle-label">{label}</span>
        {description && (
          <span className="ihn-create-toggle-desc">{description}</span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={
          "ihn-create-switch" +
          (checked ? " is-on" : "") +
          (disabled ? " is-disabled" : "")
        }
        onClick={() => onChange(!checked)}
      >
        <span className="ihn-create-switch-text">
          {checked ? "On" : "Off"}
        </span>
      </button>
    </div>
  );
}

const CREATE_STEPS = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Title" },
  { id: 3, label: "Content" },
  { id: 4, label: "Settings" },
  { id: 5, label: "Preview" },
];

const SocialLinksIcon = (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const wordCount = (value) =>
  (value || "").trim().split(/\s+/).filter(Boolean).length;

const isOverWordLimit = (value, max, prev) => {
  if (wordCount(value) > max) return true;
  if (wordCount(prev) >= max && (value || "").length > (prev || "").length)
    return true;
  return false;
};

const getYoutubeEmbedUrl = (link) => {
  const text = String(link || "").trim();
  if (!text) return "";
  const match =
    text.match(
      /(?:youtube\.com\/(?:watch\?[^#]*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
    ) || text.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : "";
};

const EDIT_FILTERS = {
  Original: "",
  Studio: "saturate(1.1) contrast(1.05)",
  Spotlight: "brightness(1.15) contrast(1.1) saturate(1.05)",
  Prime: "contrast(1.08) saturate(1.15)",
  Classic: "sepia(0.25) contrast(1.05)",
  Edge: "contrast(1.3) saturate(1.2)",
  Luminate: "brightness(1.2) contrast(0.95)",
};

const EDIT_ASPECTS = ["Original", "Free", "Square", "3:4", "4:1", "16:9"];

const ROTATE_FIX_OPTIONS = [
  { label: "None", value: 0 },
  { label: "90°", value: 90 },
  { label: "180°", value: 180 },
  { label: "270°", value: 270 },
];

const PrimaryButton = ({ children, onClick, type = "button", disabled, fullWidth, className = "" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`Community-btn Community-btn-primary ${fullWidth ? "Community-btn-full" : ""} ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, onClick, type = "button", fullWidth, disabled }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`Community-btn Community-btn-secondary ${fullWidth ? "Community-btn-full" : ""}`}
  >
    {children}
  </button>
);

const RotateFixDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = ROTATE_FIX_OPTIONS.find((o) => o.value === value);

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Rotation: {current ? current.label : "Custom"}</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {ROTATE_FIX_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === o.value}
              className={`Community-dropdown-option ${value === o.value ? "Community-dropdown-selected" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{o.label}</span>
              </span>
              {value === o.value && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FlipDropdown = ({ flipH, flipV, onToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
     return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Flip</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          <div className="Community-editor-flip">
            <button
              type="button"
              className={flipH ? "active" : ""}
              onClick={() => onToggle("flipH")}
              title="Flip Horizontal"
              aria-pressed={flipH}
            >
              <span>Flip Horizontal</span>
              {flipH && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
            <button
              type="button"
              className={flipV ? "active" : ""}
              onClick={() => onToggle("flipV")}
              title="Flip Vertical"
              aria-pressed={flipV}
            >
              <span>Flip Vertical</span>
              {flipV && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AspectRatioDropdown = ({ value, onChange, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen((v) => { if (v && onOpenChange) onOpenChange(false); return false; });
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onOpenChange]);

  return (
    <div className="Community-dropdown" ref={ref}>
      <button
        type="button"
        className="Community-dropdown-trigger"
        onClick={() => setOpen((v) => { const n = !v; if (onOpenChange) onOpenChange(n); return n; })}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>Aspect: {value}</span>
        <span aria-hidden="true">▼</span>
      </button>
      {open && (
        <div className="Community-dropdown-menu" role="menu">
          {EDIT_ASPECTS.map((a) => (
            <button
              key={a}
              type="button"
              role="menuitemradio"
              aria-checked={value === a}
              className={`Community-dropdown-option ${value === a ? "Community-dropdown-selected" : ""}`}
              onClick={() => {
                onChange(a);
                setOpen(false);
              }}
            >
              <span className="Community-dropdown-option-text">
                <span className="Community-dropdown-option-title">{a}</span>
              </span>
              {value === a && <span className="Community-dropdown-check" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ImageEditor = ({ src, onApply, onClose, initialCornerRadius = 0 }) => {
  const canvasRef = useRef(null);
  const [aspect, setAspect] = useState("Original");
  const [rotate, setRotate] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [filter, setFilter] = useState("Original");
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [vignette, setVignette] = useState(0);
  const [tab, setTab] = useState("adjust");
  const [hasCropped, setHasCropped] = useState(false);
  const [cornerRadius, setCornerRadius] = useState(initialCornerRadius);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const [stageW, setStageW] = useState(0);
  const [stageH, setStageH] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const cropDrag = useRef(null);

  useEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) return;
      setStageW(stage.offsetWidth || stage.clientWidth);
      setStageH(stage.offsetHeight || stage.clientHeight);
    };
    const t = setTimeout(measure, 40);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [tab, aspect, rotate, zoom, flipH, flipV, filter, brightness, contrast, saturation, vignette, imgRef.current ? imgRef.current.src : ""]);

  useEffect(() => {
    if (tab !== "crop") return undefined;
    const onUp = () => { cropDrag.current = null; };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [tab]);

  const rectFromPointer = (e) => {
    const rect = stageRef.current.getBoundingClientRect();
    return {
      rx: (e.clientX - rect.left) / rect.width,
      ry: (e.clientY - rect.top) / rect.height,
    };
  };

  const applyAspect = (x, y, w, h) => {
    const r = ratio;
    if (!r || h <= 0) return { x, y, w, h };
    if (w / h > r) { w = h * r; }
    else { h = w / r; }
    return { x, y, w, h };
  };

  const startCropDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const start = { mode, ...rectFromPointer(e), ...crop };
    cropDrag.current = start;
    const onMove = (ev) => {
      const p = rectFromPointer(ev);
      const px = p.rx, py = p.ry;
      const fixed = { x: start.x, y: start.y, w: start.w, h: start.h };
      let x = fixed.x, y = fixed.y, w = fixed.w, h = fixed.h;
      const r = ratio;

      if (mode === "move") {
        x = Math.min(Math.max(start.x + px - start.rx, 0), 1 - start.w);
        y = Math.min(Math.max(start.y + py - start.ry, 0), 1 - start.h);
        w = start.w; h = start.h;
        setCrop({ x, y, w, h });
        return;
      }

      const right = mode.includes("r");
      const left = mode.includes("l");
      const bottom = mode.includes("b");
      const top = mode.includes("t");

      if (right) w = Math.max(px - fixed.x, 0.05);
      if (left) { x = Math.min(px, fixed.x + fixed.w - 0.05); w = (fixed.x + fixed.w) - x; }
      if (bottom) h = Math.max(py - fixed.y, 0.05);
      if (top) { y = Math.min(py, fixed.y + fixed.h - 0.05); h = (fixed.y + fixed.h) - y; }

      if (r) {
        const rw = w / r;
        const rh = h * r;
        if (rw > h) {
          if (bottom && !top) h = rw;
          else if (top && !bottom) { h = rw; y = fixed.y + fixed.h - h; }
          else w = rh;
        } else {
          if (right && !left) w = rh;
          else if (left && !right) { w = rh; x = fixed.x + fixed.w - w; }
          else h = rw;
        }
      }

      x = Math.min(Math.max(x, 0), 1 - w);
      y = Math.min(Math.max(y, 0), 1 - h);

      setCrop({ x, y, w, h });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const aspectRatio = () => {
    const map = {
      Free: null,
      Square: 1,
      "3:4": 3 / 4,
      "4:1": 4,
      "16:9": 16 / 9,
    };
    return map[aspect];
  };
  const ratio = aspectRatio();

  const handleAspectChange = (name) => {
    setAspect(name);
    if (name === "Free") {
      setCrop({ x: 0.025, y: 0.025, w: 0.95, h: 0.95 });
      return;
    }
    const map = { Free: null, Square: 1, "3:4": 3 / 4, "4:1": 4, "16:9": 16 / 9 };
    const r = map[name];
    let w, h;
    if (!r) {
      w = Math.min(crop.w, 0.95);
      h = Math.min(crop.h, 0.95);
    } else {
      if (r > 1) { w = 0.9; h = w / r; }
      else { h = 0.9; w = h * r; }
    }
    const x = Math.max((1 - w) / 2, 0);
    const y = Math.max((1 - h) / 2, 0);
    setCrop({ x, y, w, h });
  };

  useEffect(() => {
    const img = new Image();
    if (/^https?:\/\//i.test(src)) img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => { imgRef.current = img; draw(); };
  }, [src]);

  useEffect(() => {
    if (imgRef.current) draw();
  }, [rotate, zoom, flipH, flipV, filter, brightness, contrast, saturation, vignette, cornerRadius, hasCropped]);

  const draw = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    let w = img.naturalWidth;
    let h = img.naturalHeight;

    const maxW = 560, maxH = 480;
    const scale = Math.min(maxW / w, maxH / h, 1);
    w *= scale; h *= scale;

    canvas.width = Math.round(w);
    canvas.height = Math.round(h);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    if (cornerRadius > 0) {
      const r = Math.max(1, Math.min(w, h) * (cornerRadius / 500));
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.arcTo(w, 0, w, h, r);
      ctx.arcTo(w, h, 0, h, r);
      ctx.arcTo(0, h, 0, 0, r);
      ctx.arcTo(0, 0, w, 0, r);
      ctx.closePath();
      ctx.clip();
    }
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale((flipH ? -1 : 1) * zoom, (flipV ? -1 : 1) * zoom);
    ctx.filter = [
      `brightness(${1 + brightness / 100})`,
      `contrast(${1 + contrast / 100})`,
      `saturate(${1 + saturation / 100})`,
      EDIT_FILTERS[filter],
    ].filter(Boolean).join(" ");
    const drawW = w;
    ctx.drawImage(img, -drawW / 2, -h / 2, drawW, h);

    if (vignette !== 0) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const W = canvas.width;
      const H = canvas.height;
      const a = Math.min(Math.abs(vignette) / 120, 0.85);
      const inner = Math.min(W, H) * 0.35;
      const outer = Math.max(W, H) * 1.05;
      const corners = [[0, 0], [W, 0], [0, H], [W, H]];
      const dark = vignette > 0;
      corners.forEach(([cx, cy]) => {
        const g = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer);
        g.addColorStop(0, "rgba(0,0,0,0)");
        g.addColorStop(1, dark ? `rgba(0,0,0,${a})` : `rgba(255,255,255,${a})`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });
    }
    ctx.restore();
  };

  const hasChanges =
    aspect !== "Free" ||
    rotate !== 0 ||
    zoom !== 1 ||
    flipH || flipV ||
    filter !== "Original" ||
    brightness !== 0 ||
    contrast !== 0 ||
    saturation !== 0 ||
    vignette !== 0 ||
    crop.x !== 0 || crop.y !== 0 || crop.w !== 1 || crop.h !== 1 ||
    cornerRadius !== 0;

  const revert = () => {
    setRotate(0);
    setZoom(1);
    setFlipH(false);
    setFlipV(false);
    setFilter("Original");
    setBrightness(0);
    setContrast(0);
    setSaturation(0);
    setVignette(0);
    setAspect("Original");
    setCrop({ x: 0, y: 0, w: 1, h: 1 });
    setCornerRadius(0);
    setHasCropped(false);
    setShowRevertConfirm(false);
  };

  const applyEdit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let out = canvas;
    const cropped = crop.x !== 0.1 || crop.y !== 0.1 || crop.w !== 0.8 || crop.h !== 0.8;
    if (cropped) {
      const sw = canvas.width * crop.w;
      const sh = canvas.height * crop.h;
      const sx = canvas.width * crop.x;
      const sy = canvas.height * crop.y;
      const c = document.createElement("canvas");
      c.width = Math.round(sw);
      c.height = Math.round(sh);
      const ctx = c.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, c.width, c.height);
      out = c;
    }
    let dataUrl = out.toDataURL("image/png");
    if (cornerRadius > 0) {
      const w = out.width;
      const h = out.height;
      const r = Math.max(1, Math.min(w, h) * (cornerRadius / 500));
      const rounded = document.createElement("canvas");
      rounded.width = w;
      rounded.height = h;
      const rctx = rounded.getContext("2d");
      rctx.clearRect(0, 0, w, h);
      rctx.beginPath();
      rctx.moveTo(r, 0);
      rctx.arcTo(w, 0, w, h, r);
      rctx.arcTo(w, h, 0, h, r);
      rctx.arcTo(0, h, 0, 0, r);
      rctx.arcTo(0, 0, w, 0, r);
      rctx.closePath();
      rctx.clip();
      rctx.drawImage(out, 0, 0);
      out = rounded;
      dataUrl = out.toDataURL("image/png");
    }
    if (hasCropped || cropped || rotate !== 0 || flipH || flipV || zoom !== 1 || aspect !== "Original" ||
        filter !== "Original" || brightness !== 0 || contrast !== 0 || saturation !== 0 || vignette !== 0 || cornerRadius !== 0) {
      onApply(dataUrl);
    }
    onClose();
  };

  return (
    <Overlay title="Edit image" onClose={onClose} panelClassName="Community-editor-panel">
      <div className="Community-editor-body">
        <div className="Community-editor-stage">
          <div className="Community-editor-canvas-wrap" ref={stageRef}>
            <canvas ref={canvasRef} className="Community-editor-canvas" />
            {tab === "crop" && imgRef.current && (
              <div className="Community-crop-wrap">
                {aspect !== "Original" && (
                  <div
                    className="Community-crop-overlay"
                    onPointerDown={(e) => { e.preventDefault(); startCropDrag(e, "move"); }}
                  >
                  <div className="Community-crop-mask-left" style={{ width: `${crop.x * 100}%`, height: "100%" }} />
                  <div className="Community-crop-mask-top" style={{ top: 0, left: `${crop.x * 100}%`, width: `${crop.w * 100}%`, height: `${crop.y * 100}%` }} />
                  <div className="Community-crop-mask-right" style={{ right: 0, top: 0, width: `${(1 - crop.x - crop.w) * 100}%`, height: "100%" }} />
                  <div className="Community-crop-mask-bottom" style={{ bottom: 0, left: `${crop.x * 100}%`, width: `${crop.w * 100}%`, height: `${(1 - crop.y - crop.h) * 100}%` }} />
                  <div className="Community-crop-box" style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` }}>
                    <div className="Community-crop-handle tl" onPointerDown={(e) => startCropDrag(e, "tl")} />
                    <div className="Community-crop-handle tr" onPointerDown={(e) => startCropDrag(e, "tr")} />
                    <div className="Community-crop-handle bl" onPointerDown={(e) => startCropDrag(e, "bl")} />
                    <div className="Community-crop-handle br" onPointerDown={(e) => startCropDrag(e, "br")} />
                    <div className="Community-crop-handle t" onPointerDown={(e) => startCropDrag(e, "t")} />
                    <div className="Community-crop-handle b" onPointerDown={(e) => startCropDrag(e, "b")} />
                    <div className="Community-crop-handle l" onPointerDown={(e) => startCropDrag(e, "l")} />
                    <div className="Community-crop-handle r" onPointerDown={(e) => startCropDrag(e, "r")} />
                  </div>
                </div>
                )}
            </div>
          )}
          </div>
        </div>

<div className="Community-editor-side">
          <div className="Community-editor-scroll">
            <div className="Community-editor-tabs">
            <span className="Community-editor-tabs-pill" style={{ transform: `translateX(${tab === "adjust" ? 0 : tab === "filter" ? 100 : 200}%)` }} />
            <button type="button" className={tab === "adjust" ? "active" : ""} onClick={() => setTab("adjust")}>Adjust</button>
            <button type="button" className={tab === "filter" ? "active" : ""} onClick={() => setTab("filter")}>Filter</button>
            <button type="button" className={tab === "crop" ? "active" : ""} onClick={() => setTab("crop")}>Crop</button>
          </div>

          {tab === "adjust" && (
            <div className="Community-editor-controls">
              <label className="Community-editor-row"><span>Brightness</span><input type="range" min="-100" max="100" value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} /><b>{brightness}%</b></label>
              <label className="Community-editor-row"><span>Contrast</span><input type="range" min="-100" max="100" value={contrast} onChange={(e) => setContrast(Number(e.target.value))} /><b>{contrast}%</b></label>
              <label className="Community-editor-row"><span>Saturation</span><input type="range" min="-100" max="100" value={saturation} onChange={(e) => setSaturation(Number(e.target.value))} /><b>{saturation}%</b></label>
              <label className="Community-editor-row"><span>Vignette</span><input type="range" min="-100" max="100" value={vignette} onChange={(e) => setVignette(Number(e.target.value))} /><b>{vignette}%</b></label>
            </div>
          )}

          {tab === "filter" && (
            <div className="Community-editor-filters">
              {Object.keys(EDIT_FILTERS).map((f) => (
                <button key={f} type="button" className={`Community-editor-filter ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
                  <span className="Community-editor-filter-thumb"><img src={src} alt={f} style={{ filter: EDIT_FILTERS[f] }} /></span>
                  <em>{f}</em>
                </button>
              ))}
            </div>
          )}

          {tab === "crop" && (
            <div className="Community-editor-controls">
              <span className="Community-editor-row-title">Aspect Ratio</span>
              <AspectRatioDropdown value={aspect} onChange={handleAspectChange} />
              <RotateFixDropdown value={rotate} onChange={setRotate} />
              <div className="Community-editor-rotate">
                <FlipDropdown flipH={flipH} flipV={flipV} onToggle={(which) => (which === "flipH" ? setFlipH(!flipH) : setFlipV(!flipV))} />
                <span>Rotate &amp; Zoom</span>
                <label className="Community-editor-row"><span>Rotate</span><input type="range" min="0" max="360" value={rotate} onChange={(e) => setRotate(Number(e.target.value))} /><b>{rotate}°</b></label>
                <label className="Community-editor-row"><span>Zoom</span><input type="range" min="0" max="100" step="1" value={Math.round(((zoom - 1) / 2) * 100)} onChange={(e) => setZoom(1 + (Number(e.target.value) / 100) * 2)} /><b>{Math.round(((zoom - 1) / 2) * 100)}%</b></label>
              </div>
            </div>
          )}

          </div>

          <div className="Community-editor-actions">
            <SecondaryButton onClick={() => setShowRevertConfirm(true)} disabled={!hasChanges}>Revert</SecondaryButton>
            <PrimaryButton onClick={() => setShowApplyConfirm(true)} disabled={!hasChanges}>Apply</PrimaryButton>
          </div>
        </div>
      </div>

      {showRevertConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-revert-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-revert-title">
            <div className="Community-overlay-header">
              <h2 id="Community-revert-title" className="Community-heading-section">Revert changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowRevertConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body Community-revert-body">
              <p className="Community-body-text Community-revert-text">Are you sure you want to revert all the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-revert-actions">
              <PrimaryButton onClick={revert}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {showApplyConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-confirm-center" role="dialog" aria-modal="true" aria-labelledby="Community-apply-title">
            <div className="Community-overlay-header">
              <h2 id="Community-apply-title" className="Community-heading-section">Save changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowApplyConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              <p className="Community-body-text">Are you sure you want to save the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={applyEdit}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  );
};

const BlogVideoEditor = ({ src, onApply, onClose }) => {
  const videoRef = useRef(null);
  const [rotate, setRotate] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [filter, setFilter] = useState("Original");
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [vignette, setVignette] = useState(0);
  const [tab, setTab] = useState("adjust");
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  const hasChanges = rotate !== 0 || zoom !== 1 || flipH || flipV ||
    filter !== "Original" || brightness !== 0 || contrast !== 0 || saturation !== 0 ||
    vignette !== 0;

  const revert = () => {
    setRotate(0); setZoom(1); setFlipH(false); setFlipV(false); setFilter("Original");
    setBrightness(0); setContrast(0); setSaturation(0); setVignette(0);
    setShowRevertConfirm(false);
  };

  const applyEdit = () => {
    setShowApplyConfirm(false);
    onApply({
      brightness,
      contrast,
      saturation,
      vignette,
      filter,
      rotate,
      zoom,
      flipH,
      flipV,
    });
    onClose();
  };

  return (
    <Overlay title="Edit video" onClose={onClose} panelClassName="Community-editor-panel">
      <div className="Community-body-text Community-editor-video-hint">
        Preview is live. Use Apply to save the edited video.
      </div>
      <div className="Community-editor-body">
        <div className="Community-editor-stage">
          <div className="Community-editor-canvas-wrap Community-editor-video-wrap">
            <video
              ref={videoRef}
              src={src}
              controls
              loop
              playsInline
              className="Community-editor-live-video"
              style={{
                transform: `rotate(${rotate}deg) scale(${zoom}) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
                filter: [
                  `brightness(${1 + brightness / 100})`,
                  `contrast(${1 + contrast / 100})`,
                  `saturate(${1 + saturation / 100})`,
                  EDIT_FILTERS[filter],
                ].filter(Boolean).join(" "),
              }}
            />
            {vignette !== 0 && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(${vignette > 0 ? "0,0,0" : "255,255,255"},${Math.min(Math.abs(vignette) / 120, 0.85)}) 110%)`,
                }}
              />
            )}
          </div>
        </div>

        <div className="Community-editor-side">
          <div className="Community-editor-scroll">
            <div className="Community-editor-tabs Community-editor-tabs-two">
              <span className="Community-editor-tabs-pill" style={{ transform: `translateX(${tab === "adjust" ? 0 : 100}%)` }} />
              <button type="button" className={tab === "adjust" ? "active" : ""} onClick={() => setTab("adjust")}>Adjust</button>
              <button type="button" className={tab === "filter" ? "active" : ""} onClick={() => setTab("filter")}>Filter</button>
            </div>

            {tab === "adjust" && (
              <div className="Community-editor-controls">
                <label className="Community-editor-row"><span>Brightness</span><input type="range" min="-100" max="100" value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} /><b>{brightness}%</b></label>
                <label className="Community-editor-row"><span>Contrast</span><input type="range" min="-100" max="100" value={contrast} onChange={(e) => setContrast(Number(e.target.value))} /><b>{contrast}%</b></label>
                <label className="Community-editor-row"><span>Saturation</span><input type="range" min="-100" max="100" value={saturation} onChange={(e) => setSaturation(Number(e.target.value))} /><b>{saturation}%</b></label>
                <label className="Community-editor-row"><span>Vignette</span><input type="range" min="-100" max="100" value={vignette} onChange={(e) => setVignette(Number(e.target.value))} /><b>{vignette}%</b></label>
              </div>
            )}

            {tab === "filter" && (
              <div className="Community-editor-filters">
                {Object.keys(EDIT_FILTERS).map((f) => (
                  <button key={f} type="button" className={`Community-editor-filter ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
                    <span className="Community-editor-filter-thumb" style={{ filter: EDIT_FILTERS[f] }}>
                      <video src={src} muted playsInline preload="metadata" />
                    </span>
                    <em>{f}</em>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="Community-editor-actions">
            <SecondaryButton onClick={() => setShowRevertConfirm(true)} disabled={!hasChanges}>Revert</SecondaryButton>
            <PrimaryButton onClick={() => setShowApplyConfirm(true)} disabled={!hasChanges}>Apply</PrimaryButton>
          </div>
        </div>
      </div>

      {showRevertConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-revert-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-vr-title">
            <div className="Community-overlay-header">
              <h2 id="Community-vr-title" className="Community-heading-section">Revert changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowRevertConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="Community-overlay-body Community-revert-body">
              <p className="Community-body-text Community-revert-text">Are you sure you want to revert all the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-revert-actions">
              <PrimaryButton onClick={revert}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {showApplyConfirm && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-confirm-center" role="dialog" aria-modal="true" aria-labelledby="Community-va-title">
            <div className="Community-overlay-header">
              <h2 id="Community-va-title" className="Community-heading-section">Save changes?</h2>
              <button type="button" className="Community-overlay-close-btn" onClick={() => setShowApplyConfirm(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              <p className="Community-body-text">Are you sure you want to save the changes?</p>
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={applyEdit}>Yes</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </Overlay>
  );
};

const BlogEditedVideo = ({ src, edits, className = "", aspect, poster, onPlayChange }) => {
  const clipRef = useRef(null);
  const videoRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [box, setBox] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const showPauseState = !isPlaying;

  const setPlaying = (playing) => {
    setIsPlaying(playing);
    if (onPlayChange) onPlayChange(playing);
  };

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => {
      const b = el.getBoundingClientRect();
      if (b.width > 0 && b.height > 0) setBox({ w: b.width, h: b.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const e = edits || {};
  const rot90 = ((e.rotate % 180) + 180) % 180 === 90;
  const baseAspect = meta && meta.vw ? meta.vw / meta.vh : 1;
  const outputAspect = rot90 ? 1 / baseAspect : baseAspect;

  const filter = [
    e.brightness ? `brightness(${1 + e.brightness / 100})` : "",
    e.contrast ? `contrast(${1 + e.contrast / 100})` : "",
    e.saturation ? `saturate(${1 + e.saturation / 100})` : "",
    e.filter && e.filter !== "Original" ? EDIT_FILTERS[e.filter] : "",
  ].filter(Boolean).join(" ") || undefined;

  const rotScale = rot90 ? Math.max(1, (box ? Math.max(box.w / box.h, box.h / box.w) : 1)) : 1;
  const wrapTransform = [
    e.flipH ? "scaleX(-1)" : "",
    e.flipV ? "scaleY(-1)" : "",
    e.rotate ? `rotate(${e.rotate}deg)` : "",
    rotScale !== 1 ? `scale(${rotScale.toFixed(4)})` : "",
    e.zoom && e.zoom !== 1 ? `scale(${e.zoom})` : "",
  ].filter(Boolean).join(" ") || undefined;

  const vignette = e.vignette || 0;

  return (
    <div
      ref={clipRef}
      className={`ihn-article-video ${className}`}
      style={{ width: "100%", aspectRatio: aspect || `${outputAspect}`, position: "relative", overflow: "hidden", display: "block" }}
    >
      <div style={{ position: "absolute", inset: 0, transform: wrapTransform, transformOrigin: "50% 50%" }}>
        <video
          ref={videoRef}
          src={src}
          controls
          playsInline
          poster={poster || undefined}
          preload={poster ? "metadata" : undefined}
          onLoadedMetadata={(ev) =>
            setMeta({ vw: ev.currentTarget.videoWidth, vh: ev.currentTarget.videoHeight })
          }
          onPlay={() => setPlaying(true)}
          onPlaying={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          style={{ width: "100%", height: "100%", display: "block", objectFit: "cover", filter }}
        />
        {showPauseState && (
          <div
            className="Community-thumb-poster-overlay"
            onClick={(e) => {
              e.stopPropagation();
              const v = videoRef.current;
              if (v) v.play();
            }}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              cursor: "pointer",
              pointerEvents: "auto",
              background: poster ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.25)",
            }}
          >
            {poster && (
              <img
                src={poster}
                alt="Video thumbnail"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            )}
            <span className="Community-post-play-triangle" aria-hidden="true" />
          </div>
        )}
        {vignette !== 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(${vignette > 0 ? "0,0,0" : "255,255,255"},${Math.min(Math.abs(vignette) / 120, 0.85)}) 110%)`,
            }}
          />
        )}
      </div>
    </div>
  );
};

/* ---------- Video thumbnail picker (mirrors Community behavior; blog-local) ---------- */
const FRAME_COUNT = 12;

const VideoThumbnailOverlay = ({ videos, initialThumbnails = [], onDone, onCancel }) => {
  const videoCount = videos.length;
  const [thumbnails, setThumbnails] = useState(() =>
    initialThumbnails && initialThumbnails.length === videos.length
      ? [...initialThumbnails]
      : Array(videos.length).fill(null)
  );
  const [step, setStep] = useState(0);
  const currentVideoIndex = Math.min(step, videoCount - 1);
  const video = videos[currentVideoIndex];
  const isLast = currentVideoIndex >= videoCount - 1;
  const alreadySaved = Boolean(thumbnails[currentVideoIndex]);
  const [thumbnail, setThumbnail] = useState(null);
  const [frameTime, setFrameTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [frames, setFrames] = useState([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [activeMode, setActiveMode] = useState("frame");
  const [playing, setPlaying] = useState(false);
  const [showUploadWarning, setShowUploadWarning] = useState(false);
  const [showConfirmPreview, setShowConfirmPreview] = useState(false);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const buildingRef = useRef(false);
  const framesCacheRef = useRef({});
  const buildingSrcRef = useRef(null);
  const dragTrackRef = useRef(null);

  const moveTo = (index) => {
    buildingRef.current = false;
    buildingSrcRef.current = null;
    const src = videos[index];
    if (framesCacheRef.current[src]) {
      setFrames(framesCacheRef.current[src]);
      setLoadingFrames(false);
    } else {
      setFrames([]);
      setLoadingFrames(true);
    }
    setStep(index);
    setThumbnail(null);
    setFrameTime(0);
    setShowUploadWarning(false);
    setActiveMode("frame");
  };

  const commitAndAdvance = (thumb) => {
    const next = [...thumbnails];
    next[currentVideoIndex] = thumb;
    setThumbnails(next);
    if (currentVideoIndex + 1 < videoCount) {
      moveTo(currentVideoIndex + 1);
    } else {
      onDone(next);
    }
  };

  const handleSave = () => {
    if (!thumbnail) return;
    setShowConfirmPreview(true);
  };

  const captureFrameAt = (videoEl, time) => {
    const canvas = document.createElement("canvas");
    canvas.width = videoEl.videoWidth || 640;
    canvas.height = videoEl.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  };

  const fitImageToVideo = (dataUrl) => {
    return new Promise((resolve) => {
      const v = videoRef.current;
      const w = v ? (v.videoWidth || 640) : 640;
      const h = v ? (v.videoHeight || 360) : 360;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        const scale = Math.max(w / img.width, h / img.height);
        const sw = w / scale;
        const sh = h / scale;
        const sx = (img.width - sw) / 2;
        const sy = (img.height - sh) / 2;
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  const handleSeek = (t) => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration) || v.duration <= 0) return;
    if (!isFinite(t)) return;
    setFrameTime(t);
    v.currentTime = t;
  };

  const onDragMove = (clientX) => {
    const bar = dragTrackRef.current;
    const v = videoRef.current;
    if (!bar || !v || !isFinite(v.duration) || v.duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const t = ratio * v.duration;
    if (isFinite(t)) handleSeek(t);
  };

  const buildFrameStrip = () => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration) || v.duration <= 0) return;
    const src = v.src;
    if (framesCacheRef.current[src]) {
      setFrames(framesCacheRef.current[src]);
      buildingRef.current = false;
      buildingSrcRef.current = null;
      setLoadingFrames(false);
      return;
    }
    if (buildingRef.current && buildingSrcRef.current === src) return;
    buildingRef.current = true;
    buildingSrcRef.current = src;
    setLoadingFrames(true);
    const dur = v.duration;
    const collected = [];
    const times = [];
    for (let i = 0; i < FRAME_COUNT; i++) {
      times.push((dur * i) / (FRAME_COUNT - 1));
    }
    const finish = () => {
      if (buildingRef.current && buildingSrcRef.current === src) {
        buildingRef.current = false;
        buildingSrcRef.current = null;
      }
      framesCacheRef.current[src] = [...collected];
      setFrames([...collected]);
      setLoadingFrames(false);
    };
    const seekTo = (target) =>
      new Promise((resolve) => {
        let settled = false;
        const onSeeked = () => {
          v.removeEventListener("seeked", onSeeked);
          settled = true;
          resolve();
        };
        v.addEventListener("seeked", onSeeked);
        try {
          v.currentTime = target;
        } catch (err) {
          if (!settled) {
            v.removeEventListener("seeked", onSeeked);
            settled = true;
            resolve();
          }
        }
        setTimeout(() => {
          if (!settled && buildingSrcRef.current === src) {
            v.removeEventListener("seeked", onSeeked);
            settled = true;
            resolve();
          }
        }, 300);
      });
    (async () => {
      for (let i = 0; i < times.length; i++) {
        if (buildingSrcRef.current !== src) return;
        await seekTo(times[i]);
        if (buildingSrcRef.current !== src) return;
        try {
          const data = captureFrameAt(v, v.currentTime);
          if (data) collected.push(data);
        } catch (err) {
          /* ignore a single failed frame */
        }
      }
      finish();
    })();
  };

  const handlePlayRequest = () => {
    const v = videoRef.current;
    if (!v) return;
    if (activeMode === "upload" && !thumbnail) {
      setShowUploadWarning(true);
      return;
    }
    v.play();
  };

  return (
    <div className="Community-overlay-backdrop" role="presentation">
      <div className="Community-overlay-panel Community-thumb-overlay" role="dialog" aria-modal="true" aria-labelledby="Community-thumb-title">
        <div className="Community-overlay-header">
          <h2 id="Community-thumb-title" className="Community-heading-section">Video thumbnail {videoCount > 1 ? `(${currentVideoIndex + 1}/${videoCount})` : ""}</h2>
          <button type="button" className="Community-overlay-close-btn" onClick={onCancel} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {videoCount > 1 && (
          <>
            <button
              type="button"
              className="Community-thumb-nav-btn Community-thumb-nav-left"
              onClick={() => moveTo((currentVideoIndex - 1 + videoCount) % videoCount)}
              aria-label="Previous video"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="Community-thumb-nav-btn Community-thumb-nav-right"
              onClick={() => moveTo((currentVideoIndex + 1) % videoCount)}
              aria-label="Next video"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}

        <div className="Community-overlay-body Community-thumb-body">
          <div className="Community-thumb-pill">
            <span
              className="Community-thumb-pill-slider"
              style={{ transform: `translateX(${activeMode === "frame" ? 0 : 100}%)` }}
            />
            <button
              type="button"
              className={`Community-thumb-pill-btn${activeMode === "frame" ? " Community-thumb-pill-active" : ""}`}
              onClick={() => { setActiveMode("frame"); const v = videoRef.current; if (v) setThumbnail(captureFrameAt(v, v.currentTime)); }}
            >
              Frame
            </button>
            <button
              type="button"
              className={`Community-thumb-pill-btn${activeMode === "upload" ? " Community-thumb-pill-active" : ""}`}
              onClick={() => setActiveMode("upload")}
            >
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                e.target.value = "";
                if (file) {
                  const reader = new FileReader();
                  reader.onload = async () => {
                    const fitted = await fitImageToVideo(reader.result);
                    setThumbnail(fitted);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>

          {activeMode === "upload" && (
            <div className="Community-thumb-new-row">
              <button
                type="button"
                className="Community-thumb-new-btn"
                disabled={loadingFrames}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                + New
              </button>
            </div>
          )}

          <div className="Community-thumb-player">
            <video
              ref={videoRef}
              src={video}
              controls
              muted
              onLoadedMetadata={(e) => {
                setDuration(e.currentTarget.duration || 0);
                buildFrameStrip();
              }}
              onLoadedData={buildFrameStrip}
              onCanPlay={buildFrameStrip}
              onPlay={() => {
                if (activeMode === "upload" && !thumbnail) {
                  const v = videoRef.current;
                  if (v) v.pause();
                  setShowUploadWarning(true);
                  return;
                }
                setPlaying(true);
              }}
              onPause={() => setPlaying(false)}
              onSeeked={(e) => setThumbnail(captureFrameAt(e.currentTarget, e.currentTarget.currentTime))}
            />
            {!playing && thumbnail && (
              <img
                src={thumbnail}
                alt="Thumbnail poster"
                className="Community-thumb-poster-overlay"
              />
            )}
            {!playing && !loadingFrames && (
              <button
                type="button"
                className="Community-thumb-play-btn"
                aria-label="Play video"
                onClick={handlePlayRequest}
              >
                <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
            {loadingFrames && (
              <div className="Community-thumb-spinner">
                <div className="Community-thumb-spinner-dot" />
                <div className="Community-thumb-spinner-dot" />
                <div className="Community-thumb-spinner-dot" />
              </div>
            )}
            {showUploadWarning && (
              <div className="Community-thumb-warning" role="alert">
                <p className="Community-thumb-warning-text">Please upload a thumbnail photo before playing the video.</p>
                <div className="Community-thumb-warning-actions">
                  <PrimaryButton onClick={() => { setShowUploadWarning(false); fileInputRef.current && fileInputRef.current.click(); }}>Upload Photo</PrimaryButton>
                </div>
              </div>
            )}
          </div>

          {activeMode === "frame" && (
            <>
              <div
                className="Community-thumb-strip"
                ref={dragTrackRef}
                onPointerDown={(e) => {
                  e.preventDefault();
                  setDragging(true);
                  e.currentTarget.setPointerCapture(e.pointerId);
                  onDragMove(e.clientX);
                }}
                onPointerMove={(e) => { if (dragging) onDragMove(e.clientX); }}
                onPointerUp={() => setDragging(false)}
              >
                {Array.from({ length: FRAME_COUNT }).map((_, i) => (
                  <div
                    key={i}
                    className={`Community-thumb-strip-cell${Math.round((frameTime / duration) * (FRAME_COUNT - 1)) === i ? " Community-thumb-strip-active" : ""}`}
                  >
                    {frames[i] ? (
                      <img src={frames[i]} alt={`Frame ${i + 1}`} />
                    ) : (
                      <span className="Community-thumb-strip-placeholder" />
                    )}
                  </div>
                ))}
              </div>

              <p className="Community-thumb-hint">Drag across the frames to pick a thumbnail.</p>
            </>
          )}
        </div>

        <div className="Community-overlay-actions Community-overlay-actions-center Community-thumb-actions">
          <PrimaryButton onClick={handleSave} disabled={!thumbnail || loadingFrames}>Save</PrimaryButton>
        </div>
        {alreadySaved && (
          <p className="Community-thumb-saved-hint">Thumbnail saved for video {currentVideoIndex + 1}.</p>
        )}

        {showConfirmPreview && thumbnail && (
          <div className="Community-overlay-backdrop" role="presentation">
            <div className="Community-overlay-panel Community-thumb-confirm" role="dialog" aria-modal="true" aria-labelledby="Community-thumb-confirm-title">
              <div className="Community-overlay-header Community-thumb-confirm-header">
                <h2 id="Community-thumb-confirm-title" className="Community-heading-section">Thumbnail Preview {videoCount > 1 ? `(${currentVideoIndex + 1}/${videoCount})` : ""}</h2>
                <button type="button" className="Community-overlay-close-btn" onClick={() => setShowConfirmPreview(false)} aria-label="Close">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="Community-overlay-body">
                <img src={thumbnail} alt="Thumbnail preview" className="Community-thumb-confirm-img" />
              </div>
              <div className="Community-overlay-actions Community-overlay-actions-center">
                <PrimaryButton onClick={() => { setShowConfirmPreview(false); commitAndAdvance(thumbnail); }}>Accept</PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export function BlogCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const editBlog = (location.state && location.state.editBlog) || null;
  const editIsCreated = !!(editBlog && isCreatedBlog(editBlog));
  const likesOffIds = useBlogLikesOff();
  const commentsOffIds = useBlogCommentsOff();
  const hiddenLikeIds = useHiddenLikeCounts();
  const hiddenCommentIds = useHiddenCommentCounts();

  const initCards = useMemo(
    () => (editBlog ? articleToCreateCards(editBlog) : [{ heading: "", text: "", image: "" }]),
    [editBlog]
  );
  const initPhotoCount = Math.min(5, Math.max(1, initCards.length));
  const initCategory = editBlog ? (CREATE_CATEGORY_OPTIONS.includes(editBlog.category) ? editBlog.category : "General") : "";
  const initCustomCategory = editBlog && !CREATE_CATEGORY_OPTIONS.includes(editBlog.category) ? editBlog.category : "";
  const editBlogId = editBlog ? editBlog.id : null;

  const [step, setStep] = useState(editBlog ? 2 : 1);
  const [photoCount, setPhotoCount] = useState(initPhotoCount);
  const [title, setTitle] = useState(editBlog ? editBlog.title || "" : "");
  const [subhead, setSubhead] = useState(editBlog ? editBlog.subhead || editBlog.excerpt || "" : "");
  const [category, setCategory] = useState(initCategory);
  const [customCategory, setCustomCategory] = useState(initCustomCategory);
  const [cards, setCards] = useState(initCards);
  const [thumbOverlayIndex, setThumbOverlayIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragOverRef = useRef(null);
  const cardDragRefs = useRef([]);
  const cardPhotoRefs = useRef([]);
  const [failed, setFailed] = useState({});
  const [editorIndex, setEditorIndex] = useState(null);
  const [videoEditorIndex, setVideoEditorIndex] = useState(null);
  const [videoCardPlayingIndex, setVideoCardPlayingIndex] = useState(null);
  const [youtubeConfirm, setYoutubeConfirm] = useState(null);
  const [likesOff, setLikesOff] = useState(editBlogId ? likesOffIds.has(editBlogId) : false);
  const [commentsOff, setCommentsOff] = useState(editBlogId ? commentsOffIds.has(editBlogId) : false);
  const [hideLikeCount, setHideLikeCount] = useState(editBlogId ? hiddenLikeIds.has(editBlogId) : false);
  const [hideCommentCount, setHideCommentCount] = useState(editBlogId ? hiddenCommentIds.has(editBlogId) : false);
  const [previewHideLikeCount, setPreviewHideLikeCount] = useState(false);
  const [previewHideCommentCount, setPreviewHideCommentCount] = useState(false);
  const [socials, setSocials] = useState(editBlog ? editBlog.socials || [] : []);
  const [showSocialOverlay, setShowSocialOverlay] = useState(false);
  const [errors, setErrors] = useState({});
  const [published, setPublished] = useState(null);
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false);
  const [previewVideoPlaying, setPreviewVideoPlaying] = useState(false);
  const [coverPhoto, setCoverPhoto] = useState(editBlog ? editBlog.coverPhoto || "" : "");
  const [showCoverPhotoOverlay, setShowCoverPhotoOverlay] = useState(false);
  const [coverPhotoEditorOpen, setCoverPhotoEditorOpen] = useState(false);
  const coverPhotoInputRef = useRef(null);

  useEffect(() => {
    setCards((prev) => {
      const next = prev.slice(0, photoCount);
      while (next.length < photoCount) {
        next.push({ heading: "", text: "", image: "" });
      }
      return next;
    });
  }, [photoCount]);

  const updateCard = (index, field, value) =>
    setCards((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );

  const pickCardMedia = (index, files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const picked = arr.find(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (!picked) return;
    const isVideo = picked.type.startsWith("video/");
    if (isVideo) {
      updateCard(index, "image", URL.createObjectURL(picked));
      updateCard(index, "kind", "video");
      updateCard(index, "thumbnail", "");
      updateCard(index, "mediaAspect", null);
      updateCard(index, "youtube", "");
      setFailed((prev) => ({ ...prev, [index]: false }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateCard(index, "image", reader.result);
      updateCard(index, "kind", "image");
      updateCard(index, "thumbnail", "");
      updateCard(index, "mediaAspect", null);
      updateCard(index, "youtube", "");
      setFailed((prev) => ({ ...prev, [index]: false }));
    };
    reader.readAsDataURL(picked);
  };

  const moveCard = (from, to) => {
    if (from === to || to < 0 || to >= cards.length) return;
    setCards((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const startCardDrag = (e, i) => {
    if (e.button !== 0 && e.type === "pointerdown") return;
    if (e.target.closest("input, textarea, select, button, label")) return;
    e.preventDefault();
    dragOverRef.current = i;
    setDragOverIndex(i);
    setDragIndex(i);
  };

  useEffect(() => {
    if (dragIndex === null) return undefined;
    const handleMove = (e) => {
      let over = null;
      cardDragRefs.current.forEach((el, idx) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (idx !== dragIndex && e.clientY >= r.top && e.clientY <= r.bottom) {
          over = idx;
        }
      });
      dragOverRef.current = over;
      setDragOverIndex(over);
    };
    const finish = () => {
      const over = dragOverRef.current;
      if (over !== null) moveCard(dragIndex, over);
      dragOverRef.current = null;
      setDragOverIndex(null);
      setDragIndex(null);
    };
    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", finish);
    document.addEventListener("pointercancel", finish);
    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", finish);
      document.removeEventListener("pointercancel", finish);
    };
  }, [dragIndex]);

  const removeCard = (index) => {
    if (cards.length <= 1) return;
    setCards((prev) => prev.filter((_, i) => i !== index));
    setPhotoCount((c) => Math.max(1, c - 1));
  };

  const addCard = () => {
    if (photoCount >= 5) return;
    setPhotoCount((c) => Math.min(5, c + 1));
  };

  const finalCategory = category === "General" && customCategory.trim()
    ? customCategory.trim()
    : category;
  const isDuplicateCategory = customCategory.trim().length > 0 &&
    CREATE_CATEGORY_OPTIONS.some(
      (c) => c.toLowerCase() === customCategory.trim().toLowerCase()
    );

  const composePost = () => {
    const medias = cards
      .map((c) => ({
        src: c.image.trim(),
        kind: c.kind,
        edits: c.videoEdits || null,
        thumbnail: c.thumbnail || "",
        aspect: c.mediaAspect || null,
      }))
      .filter((m) => m.src.length > 0);
    const lead = medias[0] || {
      src: CREATED_BLOG_DEFAULT_COVER,
      kind: "image",
      edits: null,
      thumbnail: "",
      aspect: null,
    };

    const images = [lead.src, ...medias.slice(1).map((m) => m.src)];
    const mediaKinds = [lead.kind, ...medias.slice(1).map((m) => m.kind)];
    const mediaEdits = [lead.edits, ...medias.slice(1).map((m) => m.edits)];
    const mediaThumbs = [lead.thumbnail, ...medias.slice(1).map((m) => m.thumbnail)];
    const mediaAspects = [lead.aspect, ...medias.slice(1).map((m) => m.aspect)];
    const blocks = [];
    let nextIndex = 0;
    cards.forEach((card, cardIndex) => {
      const hasMedia = card.image.trim().length > 0;
      if (hasMedia) {
        if (cardIndex !== 0) {
          blocks.push({
            type: mediaKinds[nextIndex] === "video" ? "video" : "image",
            imageIndex: nextIndex,
            videoEdits: mediaEdits[nextIndex] || null,
            videoThumbnail: mediaKinds[nextIndex] === "video" ? mediaThumbs[nextIndex] || "" : "",
          });
        }
        nextIndex += 1;
      }
      const ytEmbed = getYoutubeEmbedUrl(card.youtube);
      if (ytEmbed && !hasMedia) {
        blocks.push({ type: "youtube", src: ytEmbed });
      }
      if (card.heading.trim()) {
        blocks.push({ type: "heading", text: card.heading.trim() });
      }
      if (card.text.trim()) {
        blocks.push({ type: "paragraph", text: card.text.trim() });
      }
    });
    const firstText =
      cards.map((c) => c.text.trim()).filter(Boolean)[0] || title.trim();
    const excerpt =
      firstText.length > 160 ? firstText.slice(0, 160).trimEnd() + "\u2026" : firstText;
    return { images, blocks, excerpt, mediaKinds, mediaEdits, mediaThumbs, mediaAspects, hasLeadMedia: !!(cards[0] && cards[0].image.trim().length > 0), coverPhoto: coverPhoto || "" };
  };

  const goNext = () => {
    if (step === 1) {
      setErrors({});
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!title.trim()) {
        setErrors({ title: "Please enter a blog title." });
        return;
      }
      setErrors({});
      setStep(3);
      return;
    }
    if (step === 3) {
      setErrors({});
      const firstCard = cards[0];
      const firstIsUploadedVideo = Boolean(firstCard && firstCard.kind === "video");
      const firstIsYoutubeOnly = Boolean(
        firstCard && !firstCard.image.trim() && firstCard.youtube
      );
      const hasAnyCardImage = cards.some((c) => c.image.trim().length > 0);
      if (!hasAnyCardImage || firstIsYoutubeOnly) {
        setShowCoverPhotoOverlay(true);
        return;
      }
      if (firstIsUploadedVideo && (!coverPhoto || !cards[0].thumbnail)) {
        setThumbOverlayIndex(0);
        setErrors({
          general: "Please set a thumbnail for your uploaded video.",
        });
        return;
      }
      setStep(4);
      return;
    }
    setErrors({});
    setStep(step + 1);
  };

  const handlePublish = () => {
    if (!title.trim()) {
      setErrors({ general: "Please enter a blog title in step 2." });
      setStep(2);
      return;
    }
    if (!category) {
      setErrors({ category: "Please choose a category." });
      setStep(2);
      return;
    }
    if (category === "General" && !customCategory.trim()) {
      setErrors({ category: "Please enter a custom category name." });
      setStep(2);
      return;
    }
    if (category === "General" && isDuplicateCategory) {
      setErrors({ category: "This category already exists." });
      setStep(2);
      return;
    }
    setErrors({});

    const now = new Date();
    const { images, blocks, excerpt, mediaKinds, mediaEdits, mediaThumbs, mediaAspects, hasLeadMedia, coverPhoto: composedCover } = composePost();

    const blogId = editIsCreated
      ? editBlog.id
      : `created-${now.getTime()}`;
    const blogSlug = (editIsCreated ? editBlog.slug : null)
      || (editBlog && !isCreatedBlog(editBlog) ? editBlog.slug || titleToSlug(title.trim()) : null)
      || `${titleToSlug(title.trim()) || "blog"}-${now.getTime().toString().slice(-8)}`;

    const blog = {
      id: blogId,
      slug: blogSlug,
      title: title.trim(),
      subhead: subhead.trim(),
      category: finalCategory,
      excerpt,
      content: blocks,
      images,
      mediaKinds,
      hasLeadMedia,
      videoEdits: mediaEdits,
      videoThumbnails: mediaThumbs,
      mediaAspects,
      author: (editBlog && editBlog.author) || "Rahul Mehta",
      company: (editBlog && editBlog.company) || "ABC Hospitality",
      publisher: (editBlog && editBlog.publisher) || "IHN",
      date: now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      likes: (editBlog && editBlog.likes) || 0,
      comments: (editBlog && editBlog.comments) || 0,
      socials,
      coverPhoto: composedCover || (editBlog && editBlog.coverPhoto) || "",
    };

    const setStoreFlag = (store, enabledIds, enabled) => {
      if (enabled && !enabledIds.has(blog.id)) store.toggle(blog.id);
      if (!enabled && enabledIds.has(blog.id)) store.toggle(blog.id);
    };
    setStoreFlag(blogLikesOffStore, likesOffIds, likesOff);
    setStoreFlag(blogCommentsOffStore, commentsOffIds, commentsOff);
    setStoreFlag(hiddenLikesBlogsStore, hiddenLikeIds, hideLikeCount);
    setStoreFlag(hiddenCommentsBlogsStore, hiddenCommentIds, hideCommentCount);

    if (editIsCreated) {
      createdBlogsStore.update(editBlog.id, blog);
    } else if (editBlog) {
      deletedBlogsStore.add(editBlog.id);
      createdBlogsStore.add(blog);
    } else {
      createdBlogsStore.add(blog);
    }

    if (editBlog) {
      navigate(blogArticleHref({ ...blog, id: blogId }));
      return;
    }
    setPublished(blog);
  };

  if (published) {
    return (
      <div className="ihnBlog-page">
        <div className="ihnBlog-nav-search-wrap">
          <BlogNavigation activeCategory="Published" />
        </div>
        <div className="ihn-create-page">
          <div className="ihn-create-wrap">
            <div className="ihn-create-success">
              <h1 className="ihn-create-success-title">
                Your blog was published
              </h1>
              <div className="ihn-create-success-actions">
                <button
                  type="button"
                  className="ihnBlog-btn ihnBlog-btn--primary"
                  onClick={() => navigate(blogArticleHref(published))}
                >
                  View Blog
                </button>
              </div>
            </div>
          </div>
        </div>
        <BlogsBottomNav
          activeTab={null}
          onNavigate={(key) => {
            if (key === "create") navigate("/blog/create");
            else navigate("/blogs/Published", { state: { postsTab: key } });
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    );
  }

  const preview = composePost();
  const leadMediaCardIndex = cards.findIndex((c) => c.image.trim().length > 0);

  return (
    <div className="ihnBlog-page">
      <div className="ihnBlog-nav-search-wrap">
        <BlogNavigation activeCategory="Published" />
      </div>
      <div className="ihn-create-page">
        <div className="ihn-create-wrap">
          <div className="ihn-create-stepper" role="tablist" aria-label="Create steps">
            {CREATE_STEPS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={"ihn-create-step" + (step === s.id ? " is-active" : "")}
                aria-current={step === s.id ? "step" : undefined}
                onClick={() => {
                  if (s.id <= step) setStep(s.id);
                }}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="ihn-create-wizard">
            {step > 1 && (
              <div className="ihn-create-topbar">
                <button
                  type="button"
                  className="ihn-create-back-arrow"
                  aria-label="Go back"
                  title="Go back"
                  onClick={() => setStep(step - 1)}
                >
                  <span aria-hidden="true">❮</span>
                </button>
                {step === 3 && (
                  <button
                    type="button"
                    className="ihn-create-stepper-btn"
                    aria-label="More photo cards"
                    title="Add card"
                    disabled={cards.length >= 5}
                    onClick={addCard}
                  >
                    ➕
                  </button>
                )}
              </div>
            )}
            {step === 1 && (
              <section
                className="ihn-create-step-panel ihm-create-step-panel-center"
                style={{ textAlign: "center" }}
              >
                <h2 className="ihn-create-step-title" style={{ textAlign: "center" }}>
                  How many Uploads?
                </h2>
                <p
                  className="ihn-create-step-sub"
                  style={{ textAlign: "center" }}
                >
                  Each upload becomes its own card. max limit 5.
                </p>
                <div
                  className="ihn-create-big-stepper"
                  style={{
                    display: "flex",
                    width: "max-content",
                    margin: "0 auto",
                    justifyContent: "center",
                  }}
                >
                  <button
                    type="button"
                    className="ihn-create-stepper-btn"
                    aria-label="Fewer photo cards"
                    disabled={photoCount <= 1}
                    onClick={() => setPhotoCount((c) => Math.max(1, c - 1))}
                  >
                    ➖
                  </button>
                  <span className="ihn-create-stepper-value">{photoCount}</span>
                  <button
                    type="button"
                    className="ihn-create-stepper-btn"
                    aria-label="More photo cards"
                    disabled={photoCount >= 5}
                    onClick={() => setPhotoCount((c) => Math.min(5, c + 1))}
                  >
                    ➕
                  </button>
                </div>
              </section>
            )}

            {step === 2 && (
              <section className="ihn-create-step-panel">
                <h2 className="ihn-create-step-title">Title and category</h2>
                <div className="ihn-create-card">
                  <div className="ihn-create-field">
                    <label className="ihn-create-label" htmlFor="ihn-create-title">
                      Title <span className="ihn-create-required">*</span>
                    </label>
                    <input
                      id="ihn-create-title"
                      type="text"
                      className="ihn-create-input"
                      placeholder="A clear, descriptive title for your post"
                      value={title}
                      onChange={(e) => {
                        if (!isOverWordLimit(e.target.value, 100, title)) {
                          setTitle(e.target.value);
                        }
                      }}
                    />
                    {errors.title && (
                      <p className="ihn-create-error">{errors.title}</p>
                    )}
                    <div className="ihn-create-field-meta">
                      <span className="Community-post-toolbar-count">
                        {wordCount(title)}/100
                        words
                      </span>
                    </div>
                  </div>
                  <div className="ihn-create-field">
                    <label className="ihn-create-label" htmlFor="ihn-create-subhead">
                      Subheading
                    </label>
                    <input
                      id="ihn-create-subhead"
                      type="text"
                      className="ihn-create-input"
                      placeholder="A short supporting line under the title (optional)"
                      value={subhead}
                      onChange={(e) => {
                        if (!isOverWordLimit(e.target.value, 200, subhead)) {
                          setSubhead(e.target.value);
                        }
                      }}
                    />
                    <div className="ihn-create-field-meta">
                      <span className="Community-post-toolbar-count">
                        {wordCount(subhead)}/200
                        words
                      </span>
                    </div>
                  </div>
                  <div className="ihn-create-field">
                    <label className="ihn-create-label" htmlFor="ihn-create-category">
                      Category <span className="ihn-create-required">*</span>
                    </label>
                    <select
                      id="ihn-create-category"
                      className="ihn-create-input ihn-create-select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">Choose category</option>
                      {CREATE_CATEGORY_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    {category === "General" && (
                      <div className="ihn-create-general-category">
                        <label
                          className="ihn-create-label"
                          htmlFor="ihn-create-custom-category"
                        >
                          Enter Category
                        </label>
                        <input
                          id="ihn-create-custom-category"
                          type="text"
                          className="ihn-create-input"
                          placeholder="Enter a new category name"
                          value={customCategory}
                          onChange={(e) => setCustomCategory(e.target.value)}
                        />
                        {isDuplicateCategory && (
                          <p className="ihn-create-error">
                            This category already exists. Please choose a
                            different name.
                          </p>
                        )}
                      </div>
                    )}
                    {errors.category && (
                      <p className="ihn-create-error">{errors.category}</p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {step === 3 && (
              <section className="ihn-create-step-panel">
                <h2 className="ihn-create-step-title">Build your content</h2>
                <p className="ihn-create-step-sub">
                  Pick & Drag a card to reorder it.
                </p>
                <div className="ihn-create-content-cards">
                  {cards.map((card, i) => (
                    <div
                      key={i}
                      className={
                        "ihn-create-content-card" +
                        (dragIndex === i ? " ihn-create-content-card--dragging" : "") +
                        (dragOverIndex === i
                          ? " ihn-create-content-card--drag-over"
                          : "")
                      }
                      ref={(el) => (cardDragRefs.current[i] = el)}
                      onPointerDown={(e) => startCardDrag(e, i)}
                    >
                      <div className="ihn-create-content-card-head">
                        <span className="ihn-create-content-card-num">
                          {i + 1}
                        </span>
                        <span className="ihn-create-grip" aria-hidden="true" />
                        <button
                          type="button"
                          className="ihn-create-card-delete"
                          aria-label="Delete card"
                          title="Delete card"
                          disabled={cards.length <= 1}
                          onClick={() => removeCard(i)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            aria-hidden="true"
                          >
                            <path
                              fill="#d13212"
                              d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="ihn-create-content-card-grid">
                        <div className="ihn-create-content-card-left">
                          <label className="ihn-create-label" htmlFor={`ihn-create-card-${i}-heading`}>
                            Heading
                          </label>
                          <input
                            id={`ihn-create-card-${i}-heading`}
                            type="text"
                            className="ihn-create-input"
                            placeholder="Section heading"
                            value={card.heading}
                            onChange={(e) => {
                              if (!isOverWordLimit(e.target.value, 100, card.heading)) {
                                updateCard(i, "heading", e.target.value);
                              }
                            }}
                          />
                          <div className="ihn-create-field-meta">
                            <span className="Community-post-toolbar-count">
                              {wordCount(card.heading)}/100
                              words
                            </span>
                          </div>
                          <label className="ihn-create-label" htmlFor={`ihn-create-card-${i}-text`}>
                            Description
                          </label>
                          <textarea
                            id={`ihn-create-card-${i}-text`}
                            className="ihn-create-input ihn-create-textarea"
                            placeholder="Text that follows this photo (optional)"
                            rows={4}
                            value={card.text}
                            onChange={(e) => {
                              if (!isOverWordLimit(e.target.value, 200, card.text)) {
                                updateCard(i, "text", e.target.value);
                              }
                            }}
                          />
                          <div className="ihn-create-field-meta">
                            <span className="Community-post-toolbar-count">
                              {wordCount(card.text)}/200
                              words
                            </span>
                          </div>
                          <label className="ihn-create-label" htmlFor={`ihn-create-card-${i}-youtube`}>
                            YouTube
                          </label>
                          <input
                            id={`ihn-create-card-${i}-youtube`}
                            type="text"
                            className="ihn-create-input"
                            placeholder="Paste YouTube video link (optional)"
                            value={
                              youtubeConfirm && youtubeConfirm.index === i
                                ? youtubeConfirm.value
                                : (card.youtube || "")
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (card.image.trim() && getYoutubeEmbedUrl(value)) {
                                setYoutubeConfirm({ index: i, value });
                              } else {
                                updateCard(i, "youtube", value);
                              }
                            }}
                          />
                        </div>
                        <div className="ihn-create-content-card-right">
                          <span className="ihn-create-label">Media</span>
                          <div
                            className="ihn-create-photo-card-media"
                            style={card.mediaAspect ? { aspectRatio: `${card.mediaAspect}`, width: "100%" } : undefined}
                          >
                            {card.image.trim() ? (
                              card.kind === "video" ? (() => {
                                const ve = card.videoEdits || {};
                                const vFilter = [
                                  ve.brightness ? `brightness(${1 + ve.brightness / 100})` : "",
                                  ve.contrast ? `contrast(${1 + ve.contrast / 100})` : "",
                                  ve.saturation ? `saturate(${1 + ve.saturation / 100})` : "",
                                  ve.filter && ve.filter !== "Original" ? EDIT_FILTERS[ve.filter] : "",
                                ].filter(Boolean).join(" ") || undefined;
                                const vTransform = [
                                  ve.rotate ? `rotate(${ve.rotate}deg)` : "",
                                  ve.flipH ? "scaleX(-1)" : "",
                                  ve.flipV ? "scaleY(-1)" : "",
                                  ve.zoom && ve.zoom !== 1 ? `scale(${ve.zoom})` : "",
                                ].filter(Boolean).join(" ") || undefined;
                                const vig = ve.vignette || 0;
                                return (
                                  <>
                                    <video
                                      className="ihn-create-photo-card-thumb"
                                      src={card.image.trim()}
                                      controls
                                      playsInline
                                      poster={card.thumbnail || undefined}
                                      preload={card.thumbnail ? "metadata" : undefined}
                                      onLoadedMetadata={(e) => {
                                        const vw = e.currentTarget.videoWidth;
                                        const vh = e.currentTarget.videoHeight;
                                        if (vw && vh) updateCard(i, "mediaAspect", vw / vh);
                                      }}
                                      onPlay={() => setVideoCardPlayingIndex(i)}
                                      onPlaying={() => setVideoCardPlayingIndex(i)}
                                      onPause={() =>
                                        setVideoCardPlayingIndex((prev) => (prev === i ? null : prev))
                                      }
                                      onEnded={() =>
                                        setVideoCardPlayingIndex((prev) => (prev === i ? null : prev))
                                      }
                                      style={{ transform: vTransform, filter: vFilter }}
                                    />
                                    {vig !== 0 && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          inset: 0,
                                          pointerEvents: "none",
                                          background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 42%, rgba(${vig > 0 ? "0,0,0" : "255,255,255"},${Math.min(Math.abs(vig) / 120, 0.85)}) 110%)`,
                                        }}
                                      />
                                    )}
                                    {card.thumbnail && videoCardPlayingIndex !== i && (
                                      <div
                                        className="Community-thumb-poster-overlay"
                                        aria-hidden="true"
                                        style={{ zIndex: 1 }}
                                      >
                                        <img
                                          src={card.thumbnail}
                                          alt="Video thumbnail"
                                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        />
                                        <span className="Community-post-play-triangle" aria-hidden="true" />
                                      </div>
                                    )}
                                  </>
                                );
                              })()
                              : failed[i] ? (
                                <div className="ihn-create-photo-card-fallback">
                                  {(card.heading || "").trim() || "Photo failed to load"}
                                </div>
                              ) : (
                                <img
                                  className="ihn-create-photo-card-thumb"
                                  src={card.image.trim()}
                                  alt={(card.heading || "").trim() || ""}
                                  onLoad={(e) => {
                                    const nw = e.currentTarget.naturalWidth;
                                    const nh = e.currentTarget.naturalHeight;
                                    if (nw && nh) updateCard(i, "mediaAspect", nw / nh);
                                  }}
                                  onError={() => setFailed((prev) => ({ ...prev, [i]: true }))}
                                />
                              )
                            ) : getYoutubeEmbedUrl(card.youtube) ? (
                              <iframe
                                className="ihn-create-photo-card-thumb ihn-create-photo-card-youtube"
                                src={getYoutubeEmbedUrl(card.youtube)}
                                title={(card.heading || "").trim() || "YouTube video"}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                              />
                            ) : (
                              <div
                                className="ihn-create-photo-card-stage"
                                role="button"
                                tabIndex={0}
                                aria-label="Upload photo or video"
                                onClick={() =>
                                  cardPhotoRefs.current[i] &&
                                  cardPhotoRefs.current[i].click()
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    cardPhotoRefs.current[i] &&
                                      cardPhotoRefs.current[i].click();
                                  }
                                }}
                              >
                                <div className="ihn-create-photo-card-placeholder">
                                  <p>Upload photo or video</p>
                                </div>
                                <div className="ihn-create-upload-inline">
                                  <button
                                    type="button"
                                    className="ihn-create-upload-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      cardPhotoRefs.current[i] &&
                                        cardPhotoRefs.current[i].click();
                                    }}
                                  >
                                    Upload
                                  </button>
                                </div>
                              </div>
                            )}
                            {card.image.trim() ? (
                              <>
                                <button
                                  type="button"
                                  className="Community-image-delete-btn"
                                  aria-label="Remove photo"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateCard(i, "image", "");
                                    updateCard(i, "kind", "");
                                    updateCard(i, "thumbnail", "");
                                    updateCard(i, "mediaAspect", null);
                                    setFailed((prev) => ({ ...prev, [i]: false }));
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="Community-image-edit-btn"
                                  aria-label={card.kind === "video" ? "Edit video" : "Edit photo"}
                                  title={card.kind === "video" ? "Edit video" : "Edit photo"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (card.kind === "video") {
                                      setVideoEditorIndex(i);
                                    } else {
                                      setEditorIndex(i);
                                    }
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="Community-image-add-btn"
                                  aria-label="Add photo or video"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cardPhotoRefs.current[i] &&
                                      cardPhotoRefs.current[i].click();
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                  </svg>
                                </button>
                              </>
                            ) : getYoutubeEmbedUrl(card.youtube) ? (
                              <button
                                type="button"
                                className="Community-image-delete-btn"
                                aria-label="Remove YouTube video"
                                title="Remove YouTube video"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateCard(i, "youtube", "");
                                }}
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            ) : null}
                            <input
                              ref={(el) => {
                                cardPhotoRefs.current[i] = el;
                              }}
                              type="file"
                              accept="image/*,video/*"
                              className="ihn-create-file-input"
                              onChange={(e) => {
                                pickCardMedia(i, e.target.files);
                                e.target.value = "";
                              }}
                            />
                          </div>
                          {card.image.trim() &&
                            card.kind === "video" &&
                            !(card.youtube && card.youtube.trim()) && (
                              <button
                                type="button"
                                className="ihn-create-thumb-btn ihnBlog-btn ihnBlog-btn--primary"
                                aria-label="Add thumbnail"
                                title={card.thumbnail ? "Change thumbnail" : "Add thumbnail"}
                                onClick={() => setThumbOverlayIndex(i)}
                              >
                                Thumbnail
                              </button>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {step === 4 && (
              <section className="ihn-create-step-panel">
                <h2 className="ihn-create-step-title">Settings</h2>
                <div className="ihn-create-settings">
                  <BlogCreateToggle
                    label="Comments off"
                    description="Hides the comment icon entirely on your published post"
                    checked={commentsOff}
                    onChange={(v) => {
                      setCommentsOff(v);
                      if (v) setHideCommentCount(true);
                    }}
                  />
                  <BlogCreateToggle
                    label="Hide comment count"
                    description="Keeps the comment button but hides the number"
                    checked={hideCommentCount}
                    onChange={setHideCommentCount}
                    disabled={commentsOff}
                  />
                  <BlogCreateToggle
                    label="Likes off"
                    description="Hides the like icon entirely on your published post"
                    checked={likesOff}
                    onChange={(v) => {
                      setLikesOff(v);
                      if (v) setHideLikeCount(true);
                    }}
                  />
                  <BlogCreateToggle
                    label="Hide like count"
                    description="Keeps the like button but hides the number"
                    checked={hideLikeCount}
                    onChange={setHideLikeCount}
                    disabled={likesOff}
                  />
                </div>

                <h3 className="ihn-create-settings-title">Post extras</h3>
                <div className="Community-post-actions-row">
                  <button
                    type="button"
                    className="Community-comment-tool-btn Community-post-action-btn"
                    aria-label="Add social links"
                    title="Add social links"
                    onClick={() => setShowSocialOverlay(true)}
                  >
                    <span className="Community-post-action-icon" aria-hidden="true">
                      {SocialLinksIcon}
                    </span>
                    {socials.length > 0
                      ? `Social links (${socials.length})`
                      : "Social links"}
                  </button>
                </div>
              </section>
            )}

            {step === 5 && (
              <section className="ihn-create-step-panel">
                <h2 className="ihn-create-step-title">Preview and publish</h2>
                <p className="ihn-create-step-sub">
                  This is exactly how your post will look to readers.
                </p>
                <div className="ihn-create-article-preview">
                  <header className="ihn-article-header-block">
                  <div className="ihn-article-meta">
                    <span className="ihnBlog-expert__overlayMeta">
                      <span className="ihnBlog-card__overlayAvatar">RM</span>
                      <span className="ihnBlog-card__overlayNameGroup">
                        <span className="ihnBlog-card__overlayName">Rahul Mehta</span>
                        <span className="ihnBlog-card__overlayCompany">IHN</span>
                      </span>
                    </span>
                    <span className="ihn-article-meta-right">
                      <span className="ihn-article-date">
                        {formatArticleDate(new Date())}
                      </span>
                      <span className="ihn-article-more">
                        <button
                          type="button"
                          className="ihn-article-more-btn"
                          aria-label="Article options"
                          aria-haspopup="menu"
                          aria-expanded={previewMenuOpen}
                          onClick={() => setPreviewMenuOpen((v) => !v)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="5" r="1.8" />
                            <circle cx="12" cy="12" r="1.8" />
                            <circle cx="12" cy="19" r="1.8" />
                          </svg>
                        </button>
                        {previewMenuOpen && (
                          <span
                            className="Community-dotmenu-dropdown"
                            role="menu"
                            aria-label="Article options"
                          >
                            <button
                              type="button"
                              role="menuitem"
                              className="Community-dotmenu-item"
                              disabled
                            >
                              <span aria-hidden="true">✏️</span>Edit
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="Community-dotmenu-item"
                              disabled
                            >
                              <span aria-hidden="true">🔗</span>Copy link
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="Community-dotmenu-item"
                              disabled
                            >
                              <span aria-hidden="true" style={{ fontSize: "15px", lineHeight: 1 }}>⭐</span>Save
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="Community-dotmenu-item"
                              disabled
                            >
                              <span aria-hidden="true">📁</span>Archive
                            </button>
                            {!likesOff && (
                              <button
                                type="button"
                                role="menuitem"
                                className="Community-dotmenu-item"
                                onClick={() => {
                                  setPreviewHideLikeCount((v) => !v);
                                  setPreviewMenuOpen((v) => !v);
                                }}
                              >
                                <span aria-hidden="true">{previewHideLikeCount ? "❤️" : "🚫"}</span>
                                {previewHideLikeCount ? "Show likes count" : "Hide likes count"}
                              </button>
                            )}
                            {!commentsOff && (
                              <button
                                type="button"
                                role="menuitem"
                                className="Community-dotmenu-item"
                                onClick={() => {
                                  setPreviewHideCommentCount((v) => !v);
                                  setPreviewMenuOpen((v) => !v);
                                }}
                              >
                                <span aria-hidden="true">{previewHideCommentCount ? "💬" : "🚫"}</span>
                                {previewHideCommentCount ? "Show comment count" : "Hide comment count"}
                              </button>
                            )}
                            <button
                              type="button"
                              role="menuitem"
                              className="Community-dotmenu-item Community-dotmenu-item-danger"
                              disabled
                            >
                              <span aria-hidden="true">🗑️</span>Delete
                            </button>
                          </span>
                        )}
                      </span>
                    </span>
                  </div>
                  <div className="ihn-article-heading-group">
                    {finalCategory && (
                      <p className="ihn-article-category">{finalCategory}</p>
                    )}
                    <h1 className="ihn-article-title">
                      {title.trim() || "Untitled Blog"}
                    </h1>
                    {(subhead.trim() || preview.excerpt) && (
                      <p className="ihn-article-subhead">
                        {subhead.trim() || preview.excerpt}
                      </p>
                    )}
                  </div>
                  </header>
                  {preview.hasLeadMedia && (
                  <div className="ihn-article-main-image-wrap">
                    {preview.mediaKinds[0] === "video" ? (
                      <div className="ihn-article-preview-media">
                        <BlogEditedVideo
                          src={preview.images[0]}
                          edits={preview.mediaEdits[0]}
                          poster={preview.mediaThumbs[0] || ""}
                          onPlayChange={setPreviewVideoPlaying}
                        />
                        {!previewVideoPlaying && (
                          <button
                            type="button"
                            className="ihn-article-thumb-btn ihnBlog-btn ihnBlog-btn--primary"
                            aria-label="Add thumbnail"
                            title={preview.mediaThumbs[0] ? "Change thumbnail" : "Add thumbnail"}
                            onClick={() => {
                              if (leadMediaCardIndex >= 0) setThumbOverlayIndex(leadMediaCardIndex);
                            }}
                          >
                            Thumbnail
                          </button>
                        )}
                      </div>
                    ) : (
                      <img
                        className="ihn-article-main-image"
                        src={preview.images[0]}
                        alt={`Lead image for "${title.trim() || "Untitled Blog"}"`}
                        style={
                          preview.mediaAspects && preview.mediaAspects[0]
                            ? { aspectRatio: `${preview.mediaAspects[0]}`, height: "auto", objectFit: "fill" }
                            : undefined
                        }
                      />
                    )}
                  </div>
                  )}
                  <div className="ihn-article-body">
                    {preview.blocks.map((block, i) => {
if (block.type === "youtube") {
                          return (
                            <iframe
                              key={i}
                              className="ihn-article-inline-video"
                              src={block.src}
                              title="YouTube video"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              style={{ width: "100%", aspectRatio: "16 / 9" }}
                            />
                          );
                        }
                      if (block.type === "video") {
                        return (
                          <BlogEditedVideo
                            key={i}
                            src={preview.images[block.imageIndex]}
                            edits={block.videoEdits || null}
                            className="ihn-article-inline-video"
                            poster={block.videoThumbnail || ""}
                          />
                        );
                      }
                      if (block.type === "image") {
                        return (
                          <img
                            key={i}
                            className="ihn-article-inline-image"
                            src={preview.images[block.imageIndex]}
                            alt=""
                            loading="lazy"
                            style={
                              preview.mediaAspects && preview.mediaAspects[block.imageIndex]
                                ? { aspectRatio: `${preview.mediaAspects[block.imageIndex]}`, objectFit: "fill" }
                                : undefined
                            }
                          />
                        );
                      }
                      if (block.type === "heading") {
                        return (
                          <h2 key={i} className="ihn-article-section-heading">
                            {block.text}
                          </h2>
                        );
                      }
                      return (
                        <p key={i} className="ihn-article-paragraph">
                          {block.text}
                        </p>
                      );
                    })}
                  </div>
                  <div className="ihn-article-actions">
                    <div className="ihn-article-engagement">
                      <div className="Community-engagement-row">
                        {!likesOff && (
                          <span className="Community-reaction-btn">
                            <button
                              type="button"
                              className="Community-reaction-trigger Community-heart-btn"
                              title="Love this article"
                              aria-label="Love this article"
                              disabled
                            >
                              <span className="Community-reaction-btn-icon Community-heart-icon">
                                ♡
                              </span>
                              {!(hideLikeCount || previewHideLikeCount) && (
                                <span className="Community-reaction-btn-count">0</span>
                              )}
                            </button>
                          </span>
                        )}
                        {!commentsOff && (
                          <button
                            type="button"
                            className="Community-engagement-btn"
                            title="View and add comments"
                            aria-label="View and add comments"
                            disabled
                          >
                            <svg
                              className="Community-engagement-btn-icon Community-comment-empty-icon"
                              viewBox="0 0 24 24"
                              width="25"
                              height="25"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {!(hideCommentCount || previewHideCommentCount) && (
                              <span className="Community-engagement-btn-count">0</span>
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className="Community-engagement-btn"
                          title="Share this article"
                          aria-label="Share this article"
                          disabled
                        >
                          <svg
                            className="Community-sharepost-icon"
                            viewBox="0 0 24 24"
                            width="25"
                            height="25"
                            fill="#000000"
                            aria-hidden="true"
                          >
                            <path
                              d="M20.3359 3.22136L3.87333 8.70889C3.56801 8.81066 3.55033 9.23586 3.84614 9.36263L9.89655 11.9557C9.96078 11.9832 10.0347 11.9752 10.0916 11.9346L16.0235 7.69749C16.2073 7.56618 16.4338 7.79266 16.3025 7.97648L12.0654 13.9084C12.0248 13.9653 12.0168 14.0392 12.0443 14.1034L14.6374 20.1539C14.7641 20.4497 15.1893 20.432 15.2911 20.1267L20.7786 3.66408C20.8698 3.39046 20.6095 3.13015 20.3359 3.22136Z"
                              fill="currentColor"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="Community-reactions-visible-btn"
                          title="See who reacted to this article"
                          aria-label="See who reacted to this article"
                          disabled
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="25"
                            height="25"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  {socials.length > 0 && (
                    <div className="ihn-article-connect">
                      <div className="ihn-article-connect-row">
                        {socials.map((s) => {
                          const p = socialPlatformById(s.id);
                          if (!p) return null;
                          return (
                            <a
                              key={s.id}
                              className="Community-social-option"
                              href={socialHref(s)}
                              target="_blank"
                              rel="noreferrer"
                              title={`${p.label}: ${s.value}`}
                              aria-label={`Open ${p.label}`}
                            >
                              <span className="Community-social-option-icon">
                                {p.icon}
                              </span>
                            </a>
                          );
                        })}
                      </div>
                      <p className="ihn-article-connect-label">Let’s Connect</p>
                    </div>
                  )}
                  <div className="ihn-article-meta ihn-article-meta-bottom">
                    <span className="ihnBlog-expert__overlayMeta">
                      <span className="ihnBlog-card__overlayAvatar">RM</span>
                      <span className="ihnBlog-card__overlayNameGroup">
                        <span className="ihnBlog-card__overlayName">Rahul Mehta</span>
                        <span className="ihnBlog-card__overlayCompany">IHN</span>
                      </span>
                    </span>
                    <span className="ihn-article-date">
                      {formatArticleDate(new Date())}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {errors.general && (
              <p className="ihn-create-error ihn-create-error-general">
                {errors.general}
              </p>
            )}
          </div>

          <div className="ihn-create-nav">
            {step < 5 && (
              <button
                type="button"
                className="ihnBlog-btn ihnBlog-btn--primary"
                onClick={goNext}
              >
                {step === 1 ? "Proceed" : "Continue"}
              </button>
            )}
            {step === 5 && (
              <button
                type="button"
                className="ihnBlog-btn ihnBlog-btn--primary"
                onClick={handlePublish}
              >
                Publish Blog
              </button>
            )}
          </div>
        </div>
      </div>

      {showSocialOverlay && (
        <SocialLinksOverlay
          value={socials}
          onSave={(links) => {
            setSocials(links);
            setShowSocialOverlay(false);
          }}
          onClose={() => setShowSocialOverlay(false)}
        />
      )}
      {editorIndex !== null && cards[editorIndex] && (
        <ImageEditor
          src={cards[editorIndex].image}
          onClose={() => setEditorIndex(null)}
          onApply={(dataUrl) => {
            updateCard(editorIndex, "image", dataUrl);
            updateCard(editorIndex, "kind", "image");
            setFailed((prev) => ({ ...prev, [editorIndex]: false }));
          }}
        />
      )}
      {thumbOverlayIndex !== null && cards[thumbOverlayIndex] && cards[thumbOverlayIndex].image.trim() && (
        <VideoThumbnailOverlay
          videos={[cards[thumbOverlayIndex].image.trim()]}
          initialThumbnails={
            cards[thumbOverlayIndex].thumbnail ? [cards[thumbOverlayIndex].thumbnail] : []
          }
          onCancel={() => setThumbOverlayIndex(null)}
          onDone={(thumbs) => {
            const first = (thumbs && thumbs.find((t) => t)) || "";
            updateCard(thumbOverlayIndex, "thumbnail", first);
            if (first) {
              const firstCardHasMedia = !!(cards[0] && cards[0].image.trim());
              const firstCardIsYoutubeOnly = !!(
                cards[0] &&
                !cards[0].image.trim() &&
                cards[0].youtube
              );
              if (
                (!firstCardHasMedia && !firstCardIsYoutubeOnly) ||
                thumbOverlayIndex === 0
              ) {
                setCoverPhoto(first);
              }
            }
            setThumbOverlayIndex(null);
            setErrors({});
          }}
        />
      )}
      {videoEditorIndex !== null && cards[videoEditorIndex] && (
        <BlogVideoEditor
          src={cards[videoEditorIndex].image}
          onClose={() => setVideoEditorIndex(null)}
          onApply={(edits) => {
            updateCard(videoEditorIndex, "videoEdits", edits);
            setFailed((prev) => ({ ...prev, [videoEditorIndex]: false }));
          }}
        />
      )}
      {youtubeConfirm && cards[youtubeConfirm.index] && (
        <Overlay
          title="Replace uploaded media?"
          onClose={() => setYoutubeConfirm(null)}
          closeOnlyByButton
          panelClassName="Community-confirm-center"
        >
          <p className="Community-body-text">
            Adding the YouTube link will remove the current upload.
          </p>
          <div className="Community-overlay-actions Community-overlay-actions-center">
            <PrimaryButton
              onClick={() => {
                const { index, value } = youtubeConfirm;
                updateCard(index, "image", "");
                updateCard(index, "kind", "");
                updateCard(index, "thumbnail", "");
                setFailed((prev) => ({ ...prev, [index]: false }));
                updateCard(index, "youtube", value);
                setCoverPhoto("");
                setYoutubeConfirm(null);
              }}
            >
              Accept
            </PrimaryButton>
          </div>
        </Overlay>
      )}
      {showCoverPhotoOverlay && (
        <div className="Community-overlay-backdrop" role="presentation">
          <div className="Community-overlay-panel Community-limit-overlay Community-confirm-center" role="dialog" aria-modal="true" aria-labelledby="ihn-cover-photo-title">
            <div className="Community-overlay-header">
              <h2 id="ihn-cover-photo-title" className="Community-heading-section">
                Upload cover photo
              </h2>
              <button
                type="button"
                className="Community-overlay-close-btn"
                onClick={() => setShowCoverPhotoOverlay(false)}
                aria-label="Close"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="Community-overlay-body">
              <p className="Community-body-text">
                This photo will be shown on the blog card in category grids.
              </p>
              {coverPhoto ? (
                <div className="ihn-create-photo-card-media ihn-create-cover-photo-media" style={{ marginTop: 12 }}>
                  <img
                    className="ihn-create-photo-card-thumb"
                    src={coverPhoto}
                    alt="Cover preview"
                  />
                  <button
                    type="button"
                    className="Community-image-delete-btn"
                    aria-label="Remove cover photo"
                    title="Remove cover photo"
                    onClick={() => setCoverPhoto("")}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="Community-image-edit-btn"
                    aria-label="Edit cover photo"
                    title="Edit cover photo"
                    onClick={() => setCoverPhotoEditorOpen(true)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="Community-image-add-btn"
                    aria-label="Replace cover photo"
                    onClick={() => coverPhotoInputRef.current && coverPhotoInputRef.current.click()}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="ihn-create-photo-card-media" style={{ marginTop: 12 }}>
                  <div
                    className="ihn-create-photo-card-stage"
                    role="button"
                    tabIndex={0}
                    aria-label="Upload cover photo"
                    onClick={() => coverPhotoInputRef.current && coverPhotoInputRef.current.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        coverPhotoInputRef.current && coverPhotoInputRef.current.click();
                      }
                    }}
                  >
                    <div className="ihn-create-photo-card-placeholder">
                      <p>Upload cover photo</p>
                    </div>
                    <div className="ihn-create-upload-inline">
                      <button
                        type="button"
                        className="ihn-create-upload-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          coverPhotoInputRef.current && coverPhotoInputRef.current.click();
                        }}
                      >
                        Upload
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <input
                ref={coverPhotoInputRef}
                type="file"
                accept="image/*"
                className="ihn-create-file-input"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = "";
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                      setCoverPhoto(reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
            <div className="Community-overlay-actions Community-overlay-actions-center">
              <PrimaryButton onClick={() => { setShowCoverPhotoOverlay(false); setStep(4); }} disabled={!coverPhoto}>
                Upload
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
      {coverPhotoEditorOpen && coverPhoto && (
        <ImageEditor
          src={coverPhoto}
          onClose={() => setCoverPhotoEditorOpen(false)}
          onApply={(dataUrl) => {
            setCoverPhoto(dataUrl);
            setCoverPhotoEditorOpen(false);
          }}
        />
      )}
      <BlogsBottomNav
        activeTab={null}
        onNavigate={(key) => {
          if (key === "create") navigate("/blog/create");
          else navigate("/blogs/Published", { state: { postsTab: key } });
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    </div>
  );
}

/* ============================================================
   6. INDIVIDUAL BLOG ARTICLE PAGE
   Data-driven article page. Cards on the listing above navigate
   to /blog/<slug>; this component resolves the matching entry via
   `resolveArticle` (backed by the shared BLOG_POSTS / BLOGS
   constants) and renders it. The structured `ARTICLE` sample is
   used as the fallback when a slug does not match anything yet.
   ============================================================ */

/* ---------- Content renderer ---------- */

function ArticleContentBlock({ block, images, aspects }) {
  switch (block.type) {
    case "paragraph":
      return <p className="ihn-article-paragraph">{block.text}</p>;

    case "heading":
      return (
        <h2 className="ihn-article-section-heading">{block.text}</h2>
      );

    case "quote":
      return (
        <blockquote className="ihn-article-quote">{block.text}</blockquote>
      );

    case "youtube":
      return (
        <iframe
          className="ihn-article-inline-video"
          src={block.src}
          title="YouTube video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{ width: "100%", aspectRatio: "16 / 9" }}
        />
      );

    case "image": {
      const src = images[block.imageIndex];
      if (!src) return null;
      return (
        <figure className="ihn-article-inline-image-wrap">
          <img
            className="ihn-article-inline-image"
            src={src}
            alt={block.alt || ""}
            loading="lazy"
            style={
              aspects && aspects[block.imageIndex]
                ? { aspectRatio: `${aspects[block.imageIndex]}`, objectFit: "fill" }
                : undefined
            }
          />
        </figure>
      );
    }

    case "video": {
      const src = images[block.imageIndex];
      if (!src) return null;
      return (
        <BlogEditedVideo
          src={src}
          edits={block.videoEdits || null}
          className="ihn-article-inline-video"
          poster={block.videoThumbnail || ""}
        />
      );
    }

    default:
      return null;
  }
}

// Converts a resolved article into renderable content blocks. Structured
// articles (the ARTICLE sample) already have a `content` block array; tabular
// dataset entries carry a plain `content` string that is split into paragraphs,
// with any extra images interleaved after paragraphs when available.
function buildArticleBlocks(article) {
  if (Array.isArray(article.content)) return article.content;

  const blocks = String(article.content || "")
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((text) => ({ type: "paragraph", text }));

  const extraImages = (article.images || []).slice(1);
  if (extraImages.length === 0) return blocks;

  const merged = [];
  blocks.forEach((block, i) => {
    merged.push(block);
    if (i < extraImages.length) {
      merged.push({ type: "image", imageIndex: i + 1, alt: "" });
    }
  });
  return merged;
}

/* ---------- Engagement bar (like / comment / share) ----------
   Reuses the Community engagement row markup + styles so behaviour
   and look match the Community/Ads pages. */

const ARTICLE_REACTION_TYPES = [
  REACTION_TYPES.LIKE,
  REACTION_TYPES.CELEBRATE,
  REACTION_TYPES.SUPPORT,
  REACTION_TYPES.LOVE,
  REACTION_TYPES.INSIGHTFUL,
  REACTION_TYPES.FUNNY,
];

const ARTICLE_REACTOR_POOL = [
  { memberId: 2, name: "Priya Sharma", company: "The Grand Palace" },
  { memberId: 3, name: "Amit Verma", company: "Verma Resorts" },
  { memberId: 4, name: "Neha Kapoor", company: "Kapoor Events" },
  { memberId: 5, name: "Karan Malhotra", company: "Malhotra Group" },
  { memberId: 6, name: "Rohit Verma", company: "Northline Hospitality" },
  { memberId: 7, name: "Sneha Kapoor", company: "IHN Lifestyle" },
  { memberId: 8, name: "Anjali Sharma", company: "The Grand Palace" },
  { memberId: 9, name: "Vikram Singh", company: "Singh Hospitality Ventures" },
  { memberId: 10, name: "Ritu Nair", company: "Nair Leisure Group" },
  { memberId: 11, name: "Karan Vij", company: "Marriott India" },
  { memberId: 12, name: "Tara Sen", company: "Pullman Gurgaon" },
  { memberId: 13, name: "Ishita Rao", company: "Radisson Blu" },
  { memberId: 14, name: "Devika Rao", company: "The Oberoi Group" },
  { memberId: 15, name: "Naina Chopra", company: "Novotel Delhi" },
  { memberId: 16, name: "Arjun Malhotra", company: "Taj Hotels" },
  { memberId: 17, name: "Meera Kapoor", company: "Hyatt Regency" },
  { memberId: 18, name: "Rohan Sethi", company: "Hilton India" },
  { memberId: 19, name: "Ananya Bose", company: "Grand Hyatt Mumbai" },
];

const ARTICLE_REACTOR_TIME_POOL = ["1h", "2h", "3h", "5h", "8h", "12h", "1d", "2d", "3d", "4d"];

// Simple deterministic string hash so each article gets its own stable mix
// of reactors (authors, reaction types and times) across renders.
function articleSlugHash(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) {
    h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return h;
}

const reactionRecency = (label) => {
  const s = String(label || "").trim().toLowerCase();
  if (s === "just now") return 0;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(s|m|h|d|w)$/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const n = parseFloat(m[1]);
  return n * { s: 1, m: 60, h: 3600, d: 86400, w: 604800 }[m[2]];
};

const sortReactionsByRecency = (reactions = []) =>
  [...reactions].sort((a, b) => reactionRecency(a.time) - reactionRecency(b.time));

// Builds a deterministic reactor list that always matches the article's
// like count, cycling through every reaction type so the Insights overlay
// shows a balanced mix (all tabs present for large counts, partial for
// small counts). `myReaction` (when the reader has reacted) is appended so
// the overlay total stays in sync with the engagement-bar counter.
function buildArticleReactors(article, myReaction) {
  const count = Math.max(0, Number(article.likes) || 0);
  const slug = article.slug || titleToSlug(article.title);
  const seed = articleSlugHash(slug);
  const reactors = [];
  for (let i = 0; i < count; i++) {
    const person = ARTICLE_REACTOR_POOL[(seed + i) % ARTICLE_REACTOR_POOL.length];
    const type = ARTICLE_REACTION_TYPES[(seed + i) % 6];
    reactors.push({
      id: `ar-${slug}-${i}`,
      memberId: person.memberId,
      name: person.name,
      company: person.company,
      type,
      time: ARTICLE_REACTOR_TIME_POOL[(seed + i) % ARTICLE_REACTOR_TIME_POOL.length],
    });
  }
  if (myReaction) {
    reactors.push({
      id: `me-${slug}`,
      memberId: CURRENT_USER_ID,
      name: getMemberById(CURRENT_USER_ID)?.name || "You",
      company: getMemberById(CURRENT_USER_ID)?.company || "",
      type: myReaction,
      time: "just now",
    });
  }
  return reactors;
}

const ARTICLE_COMMENT_AUTHOR_POOL = [
  { memberId: 2, name: "Priya Sharma", company: "The Grand Palace" },
  { memberId: 3, name: "Amit Verma", company: "Verma Resorts" },
  { memberId: 4, name: "Rhea Mathur", company: "JW Marriott" },
  { memberId: 5, name: "Karan Malhotra", company: "Malhotra Stays" },
  { memberId: 6, name: "Sneha Kapoor", company: "IHN Lifestyle" },
  { memberId: 7, name: "Vikram Singh", company: "Marriott" },
  { memberId: 8, name: "Ishita Rao", company: "Radisson Blu" },
  { memberId: 9, name: "Devika Rao", company: "The Oberoi Group" },
  { memberId: 10, name: "Naina Chopra", company: "Novotel Delhi" },
  { memberId: 11, name: "Karan Vij", company: "Marriott India" },
  { memberId: 12, name: "Tara Sen", company: "Pullman Gurgaon" },
];

const ARTICLE_COMMENT_TEXT_POOL = [
  "Fantastic read! The point about rebuilding trust through transparent communication really resonated with me. Thanks for sharing.",
  "We implemented a similar approach last quarter and the response from returning guests was noticeably warmer. Highly recommend this strategy.",
  "Insightful article. I would love to see a follow-up covering how smaller boutique properties can adapt these ideas on a tighter budget.",
  "Bookmarked this for our next team review. The practical examples make it easy to translate into day-to-day operations.",
  "This is exactly the kind of perspective our industry needs more of. Really well written and researched.",
  "Shared this with our operations team this morning. The bit about consistency really lands when you have seen it fail first-hand.",
  "Excellent breakdown. We have been slowly moving toward this philosophy and the difference in guest feedback has been clear.",
  "One of the best pieces I have read on this topic in a while. Thank you for putting it together so clearly.",
  "Completely agree. We tried a smaller version of this last year and even at a small scale it changed how our staff approached the day.",
  "Great to see this in print. Our experience across two properties lines up almost exactly with what is described here.",
  "The examples here are far more useful than the theory. I will be referencing this during our next management meeting.",
  "Thanks for including practical detail rather than just trends. We are fortunate to be testing a few of these ideas right now.",
];

// Builds one deterministic seed comment per unit of the article's static count.
// IDs are stable per article, so deletion tracking via `commentStore` stays in
// sync between the article engagement bar and the category grid cards.
function buildArticleCommentSeeds(article) {
  const count = Math.max(0, Number(article.comments) || 0);
  const slug = article.slug || titleToSlug(article.title);
  const seeds = [];
  for (let i = 0; i < count; i++) {
    const author = ARTICLE_COMMENT_AUTHOR_POOL[i % ARTICLE_COMMENT_AUTHOR_POOL.length];
    seeds.push({
      id: `article-seed-${slug}-${i}`,
      memberId: author.memberId,
      name: author.name,
      company: author.company,
      text: ARTICLE_COMMENT_TEXT_POOL[i % ARTICLE_COMMENT_TEXT_POOL.length],
      timestamp: `${(i % 47) + 1}h`,
    });
  }
  return seeds;
}

const articleCommentEntityId = (blog) =>
  `article-${blog.slug || titleToSlug(blog.title)}`;

function ArticleReactionPicker({ onPick, onHoverOut }) {
  return (
    <div
      className="Community-reaction-picker"
      onMouseLeave={onHoverOut}
      role="menu"
      aria-label="Choose a reaction"
    >
      {ARTICLE_REACTION_TYPES.map((t) => (
        <button
          key={t}
          type="button"
          className="Community-reaction-option"
          title={REACTION_LABELS[t]}
          aria-label={REACTION_LABELS[t]}
          role="menuitem"
          onClick={(e) => {
            e.stopPropagation();
            onPick(t);
          }}
        >
          {t === REACTION_TYPES.LIKE ? "👍" : REACTION_ICONS[t]}
          <span className="Community-reaction-tooltip">{REACTION_LABELS[t]}</span>
        </button>
      ))}
    </div>
  );
}

/* ---------- Reactions overlay (who reacted to the article) ----------
   Mirrors the Community "Insights" overlay: a modal with reaction tabs
   instead of an expanding inline panel. */

const ARTICLE_REACTION_VIEW_TABS = [
  { value: "like", label: "👍 Like" },
  { value: "celebrate", label: "👏 Celebrate" },
  { value: "support", label: "🤝 Support" },
  { value: "love", label: "❤️ Love" },
  { value: "insightful", label: "💡 Insightful" },
  { value: "funny", label: "😂 Funny" },
];

const ARTICLE_POSTER_ID = 1001;

const ArticleReactionListItem = ({ r, canFollow, followed, onToggleFollow }) => {
  const member = getMemberById(r.memberId) || { name: r.name, avatar: "" };
  const initials = member.avatar || r.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const icon = r.type === "like" ? "👍" : REACTION_ICONS[r.type];
  return (
    <li className="Community-viewer-item Community-reaction-item">
      <span className="Community-reaction-avatar">
        <span className="Community-member-avatar Community-comment-avatar" aria-hidden="true">{initials}</span>
        <span className="Community-reaction-badge" aria-hidden="true">{icon}</span>
      </span>
      <div className="Community-reaction-info">
        <div className="Community-reaction-name-row">
          <span className="Community-viewer-name">{r.name}</span>
          <span className="Community-reaction-time">{r.time}</span>
        </div>
        {r.company && <span className="Community-reaction-company">{r.company}</span>}
      </div>
      {canFollow && (
        <button
          type="button"
          className={`Community-follow-btn ${followed ? "Community-following" : ""}`}
          onClick={() => onToggleFollow(r.memberId)}
        >
          {followed ? (
            <>
              <span className="Community-follow-tick" aria-hidden="true">✓</span>
              Following
            </>
          ) : (
            <>
              <span className="Community-follow-plus" aria-hidden="true">+</span>
              Follow
            </>
          )}
        </button>
      )}
    </li>
  );
};

function ArticleReactionsOverlay({ reactions, posterId, onClose }) {
  const [tab, setTab] = useState("all");
  const [followedMembers, setFollowedMembers] = useState(new Set());
  const mobileTabsRef = useRef(null);
  const [indicator, setIndicator] = useState(null);

  const toggleFollowMember = (memberId) => {
    setFollowedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const selectTab = (value) => {
    setTab(value);
    const bar = mobileTabsRef.current;
    const btn = bar?.querySelector(`[data-tab="${value}"]`);
    btn?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const measureTabIndicator = () => {
    const bar = mobileTabsRef.current;
    const activeBtn = bar?.querySelector(".Community-toggle-active");
    if (bar && activeBtn) {
      const barRect = bar.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      setIndicator({
        left: btnRect.left - barRect.left + bar.scrollLeft,
        width: btnRect.width,
      });
    } else {
      setIndicator(null);
    }
  };

  useEffect(() => {
    measureTabIndicator();
    window.addEventListener("resize", measureTabIndicator);
    return () => window.removeEventListener("resize", measureTabIndicator);
  }, [tab, reactions.length]);

  let content;
  if (tab === "all") {
    content =
      reactions.length === 0 ? null : (
        <div>
          <p className="Community-label">Reactions ({reactions.length})</p>
          <ul className="Community-viewer-list">
            {reactions.map((r) => (
              <ArticleReactionListItem
                key={r.id}
                r={r}
                canFollow={posterId !== r.memberId}
                followed={followedMembers.has(r.memberId)}
                onToggleFollow={toggleFollowMember}
              />
            ))}
          </ul>
        </div>
      );
  } else {
    const filtered = reactions.filter((r) => r.type === tab);
    content = (
      <div>
        <p className="Community-label">{REACTION_LABELS[tab]} ({filtered.length})</p>
        <ul className="Community-viewer-list">
          {filtered.length === 0 ? (
            <li className="Community-viewer-item">
              <span className="Community-viewer-name">No {REACTION_LABELS[tab].toLowerCase()} reactions yet</span>
            </li>
          ) : (
            filtered.map((r) => (
              <ArticleReactionListItem
                key={r.id}
                r={r}
                canFollow={posterId !== r.memberId}
                followed={followedMembers.has(r.memberId)}
                onToggleFollow={toggleFollowMember}
              />
            ))
          )}
        </ul>
      </div>
    );
  }

  return (
    <Overlay title="Insights" onClose={onClose} wide closeOnlyByButton panelClassName="Community-overlay-insights">
      <div className="Community-views-tabs" ref={mobileTabsRef}>
        <button
          type="button"
          data-tab="all"
          className={`Community-toggle-btn Community-toggle-btn-reaction Community-mobile-tab ${tab === "all" ? "Community-toggle-active" : ""}`}
          onClick={() => selectTab("all")}
        >
          <span className="Community-mobile-tab-label">All</span>
          <span className="Community-mobile-count">{reactions.length}</span>
        </button>
        {ARTICLE_REACTION_VIEW_TABS.filter((t) => reactions.some((r) => r.type === t.value)).map((t) => {
          const icon = t.value === "like" ? "👍" : REACTION_ICONS[t.value];
          const count = reactions.filter((r) => r.type === t.value).length;
          return (
            <button
              key={t.value}
              type="button"
              data-tab={t.value}
              className={`Community-toggle-btn Community-toggle-btn-reaction Community-mobile-tab ${tab === t.value ? "Community-toggle-active" : ""}`}
              onClick={() => selectTab(t.value)}
            >
              <span className="Community-toggle-reaction-icon">{icon}</span>
              <span className="Community-mobile-count">{count}</span>
            </button>
          );
        })}
        <span
          className="Community-views-tab-indicator"
          aria-hidden="true"
          style={indicator ? { transform: `translateX(${indicator.left}px)`, width: indicator.width } : { opacity: 0 }}
        />
      </div>
      {content}
    </Overlay>
  );
}

function ArticleEngagementBar({ article, onOpenComments, commentCount, hideLikeCount = false, hideCommentCount = false, likesOff = false, commentsOff = false }) {
  const likedBlogIds = useLikedBlogIds();
  const [liked, setLiked] = useState(() =>
    likedBlogIds.has(article.id)
  );
  const [likeCount, setLikeCount] = useState(
    () => (article.likes || 0) + (likedBlogIds.has(article.id) ? 1 : 0)
  );
  const [reactionType, setReactionType] = useState(() =>
    likedBlogIds.has(article.id) ? REACTION_TYPES.LIKE : null
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reactorsOpen, setReactorsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handlePick = (type) => {
    if (reactionType === type) {
      setReactionType(null);
      setLiked(false);
      setLikeCount((c) => Math.max(0, c - 1));
      likedBlogsStore.toggle(article.id);
      return;
    }
    if (!reactionType) {
      setLikeCount((c) => c + 1);
      likedBlogsStore.toggle(article.id);
    }
    setReactionType(type);
    setLiked(true);
  };

  const displayReactions = useMemo(
    () => sortReactionsByRecency(buildArticleReactors(article, reactionType)),
    [article, reactionType]
  );

  const shownIcon = reactionType
    ? reactionType === REACTION_TYPES.LIKE
      ? "👍"
      : REACTION_ICONS[reactionType]
    : "♡";

  const handleShare = async () => {
    const url = window.location.href;
    const text = `${article.title} — Read on IHN`;

    const copyFallback = async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement("textarea");
          ta.value = url;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2600);
      } catch (_) {}
    };

    try {
      if (navigator.share) {
        await navigator.share({ title: document.title, text, url });
      } else {
        await copyFallback();
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      await copyFallback();
    }
  };

  return (
    <div className="ihn-article-engagement">
      <div className="Community-engagement-row">
        {!likesOff && (
        <span
          className={`Community-reaction-btn ${reactionType ? "Community-reaction-open" : ""}`}
          onMouseEnter={() => setPickerOpen(true)}
          onMouseLeave={() => setPickerOpen(false)}
        >
          {pickerOpen && (
            <ArticleReactionPicker onPick={handlePick} onHoverOut={() => setPickerOpen(false)} />
          )}
          <button
            type="button"
            className={`Community-reaction-trigger Community-heart-btn ${liked ? "Community-heart-liked" : ""}`}
            onClick={() => handlePick(reactionType || REACTION_TYPES.LOVE)}
            title={liked ? "Unlike" : "Love this article"}
            aria-label={`${likeCount} ${liked ? "love" : "love"}. Click to ${liked ? "unlike" : "love"}.`}
            aria-pressed={liked}
          >
            <span className="Community-reaction-btn-icon Community-heart-icon">{shownIcon}</span>
            {!hideLikeCount && likeCount > 0 && <span className="Community-reaction-btn-count">{likeCount}</span>}
          </button>
        </span>
        )}

        {!commentsOff && (
        <button
          type="button"
          className="Community-engagement-btn"
          onClick={onOpenComments}
          title="View and add comments"
          aria-label={`${commentCount} comments. Click to view comments.`}
        >
          <svg className="Community-engagement-btn-icon Community-comment-empty-icon" viewBox="0 0 24 24" width="25" height="25" fill="none" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {!hideCommentCount && commentCount > 0 && (
            <span className="Community-engagement-btn-count">{commentCount}</span>
          )}
        </button>
        )}

        <button
          type="button"
          className="Community-engagement-btn"
          onClick={handleShare}
          title="Share this article"
          aria-label="Share this article"
        >
          <svg className="Community-sharepost-icon" viewBox="0 0 24 24" width="25" height="25" fill="#000000" aria-hidden="true">
            <path d="M20.3359 3.22136L3.87333 8.70889C3.56801 8.81066 3.55033 9.23586 3.84614 9.36263L9.89655 11.9557C9.96078 11.9832 10.0347 11.9752 10.0916 11.9346L16.0235 7.69749C16.2073 7.56618 16.4338 7.79266 16.3025 7.97648L12.0654 13.9084C12.0248 13.9653 12.0168 14.0392 12.0443 14.1034L14.6374 20.1539C14.7641 20.4497 15.1893 20.432 15.2911 20.1267L20.7786 3.66408C20.8698 3.39046 20.6095 3.13015 20.3359 3.22136Z" fill="currentColor" />
          </svg>
          {copied && <span className="Community-engagement-btn-count">Copied</span>}
        </button>

        <button
          type="button"
          className="Community-reactions-visible-btn"
          onClick={() => setReactorsOpen(true)}
          title="See who reacted to this article"
          aria-label="See who reacted to this article"
        >
          <svg viewBox="0 0 24 24" width="25" height="25" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </button>
      </div>

      {copied && (
        <p className="ihn-article-copy-notice" role="status">
          Link copied — paste it anywhere to share this article.
        </p>
      )}

      {reactorsOpen && (
        <ArticleReactionsOverlay
          reactions={displayReactions}
          posterId={ARTICLE_POSTER_ID}
          onClose={() => setReactorsOpen(false)}
        />
      )}
    </div>
  );
}

export function BlogArticlePage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const commentsRef = useRef(null);
  const [articleMenuOpen, setArticleMenuOpen] = useState(false);
  const articleMenuRef = useRef(null);
  const [reportArticle, setReportArticle] = useState(null);
  const [toast, setToast] = useState(null);
  const toastIdRef = useRef(0);
  const archivedBlogIds = useArchivedBlogIds();
  const deletedBlogIds = useDeletedBlogIds();
  const savedBlogIds = useSavedBlogIds();
  const hiddenLikeCounts = useHiddenLikeCounts();
  const hiddenCommentCounts = useHiddenCommentCounts();

  const article = useMemo(() => {
    const deletedIds = deletedBlogIds;
    const matchingCreated = createdBlogsStore
      .getAll()
      .find(
        (b) =>
          !deletedIds.has(b.id) &&
          ((b.slug && b.slug === slug) || titleToSlug(b.title) === slug)
      );
    return matchingCreated || resolveArticle(slug);
  }, [slug, deletedBlogIds]);
  const contentBlocks = useMemo(() => buildArticleBlocks(article), [article]);
  const commentSeeds = useMemo(() => buildArticleCommentSeeds(article), [article]);
  const entityId = useMemo(() => articleCommentEntityId(article), [article]);
  const liveCommentCount = useCommentCount(commentSeeds, entityId);
  const isOwnerArticle =
    article.author === "Rahul Mehta" && article.company === "ABC Hospitality";
  const isArchived = archivedBlogIds.has(article.id);
  const isDeleted = deletedBlogIds.has(article.id);
  const isSaved = savedBlogIds.has(article.id);
  const isLikeCountHidden = hiddenLikeCounts.has(article.id);
  const isCommentCountHidden = hiddenCommentCounts.has(article.id);
  const likesOffIds = useBlogLikesOff();
  const commentsOffIds = useBlogCommentsOff();
  const likesOff = likesOffIds.has(article.id);
  const commentsOff = commentsOffIds.has(article.id);

  const showToast = useCallback((message) => {
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, message });
  }, []);

  const handleArchiveArticle = useCallback(() => {
    const wasArchived = archivedBlogsStore.toggle(article.id);
    setArticleMenuOpen(false);
    showToast(wasArchived ? "Blog unarchived" : "Blog archived");
  }, [article.id, showToast]);

  const handleSaveArticle = useCallback(() => {
    const wasSaved = savedBlogsStore.toggle(article.id);
    setArticleMenuOpen(false);
    showToast(wasSaved ? "Blog removed from saved" : "Blog saved");
  }, [article.id, showToast]);

  const handleCopyArticleLink = useCallback(async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setArticleMenuOpen(false);
      showToast("Link copied");
    } catch (_) {}
  }, [showToast]);

  const handleToggleHideLikes = useCallback(() => {
    hiddenLikesBlogsStore.toggle(article.id);
    setArticleMenuOpen(false);
    showToast(isLikeCountHidden ? "Likes count visible" : "Likes count hidden");
  }, [article.id, isLikeCountHidden, showToast]);

  const handleToggleHideCommentCounts = useCallback(() => {
    hiddenCommentsBlogsStore.toggle(article.id);
    setArticleMenuOpen(false);
    showToast(isCommentCountHidden ? "Comment count visible" : "Comment count hidden");
  }, [article.id, isCommentCountHidden, showToast]);

  const handleToggleLikesOff = useCallback(() => {
    const wasOff = likesOff;
    blogLikesOffStore.toggle(article.id);
    if (!wasOff) {
      if (!hiddenLikesBlogsStore.has(article.id)) hiddenLikesBlogsStore.toggle(article.id);
    } else {
      if (hiddenLikesBlogsStore.has(article.id)) hiddenLikesBlogsStore.toggle(article.id);
    }
    setArticleMenuOpen(false);
    showToast(wasOff ? "Likes turned on" : "Likes turned off");
  }, [article.id, likesOff, showToast]);

  const handleToggleCommentsOff = useCallback(() => {
    const wasOff = commentsOff;
    blogCommentsOffStore.toggle(article.id);
    if (!wasOff) {
      if (!hiddenCommentsBlogsStore.has(article.id)) hiddenCommentsBlogsStore.toggle(article.id);
    } else {
      if (hiddenCommentsBlogsStore.has(article.id)) hiddenCommentsBlogsStore.toggle(article.id);
    }
    setArticleMenuOpen(false);
    showToast(wasOff ? "Comments turned on" : "Comments turned off");
  }, [article.id, commentsOff, showToast]);

  const handleNotInterested = useCallback(() => {
    const wasHidden = hiddenBlogsStore.toggle(article.id);
    setArticleMenuOpen(false);
    showToast(wasHidden ? "Article unhidden" : "Article hidden");
    if (!wasHidden) {
      navigate(`/blogs/${article.category}`);
    }
  }, [article.id, article.category, navigate, showToast]);

  const handleReportArticle = useCallback(() => {
    setArticleMenuOpen(false);
    setReportArticle(article);
  }, [article]);

  const handleDeleteArticle = useCallback(() => {
    if (isDeleted) return;
    deletedBlogsStore.add(article.id);
    if (createdBlogsStore.getAll().some((b) => b.id === article.id)) {
      createdBlogsStore.remove(article.id);
    }
    setArticleMenuOpen(false);
    showToast("Blog deleted");
    navigate("/blogs/Published");
  }, [article.id, isDeleted, showToast, navigate]);

  const handleCommentClick = useCallback(() => {
    setCommentsOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (commentsOpen) {
      window.requestAnimationFrame(() => {
        commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [commentsOpen]);

  useEffect(() => {
    if (!articleMenuOpen) return;
    const handler = (e) => {
      if (articleMenuRef.current && !articleMenuRef.current.contains(e.target)) {
        setArticleMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [articleMenuOpen]);

  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash !== "#comments") return;
      setCommentsOpen(true);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          commentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  return (
    <div className="ihnBlog-page ihn-article-page">
      <div className="ihnBlog-nav-search-wrap">
        <BlogNavigation activeCategory={article.category} />
      </div>

      <article className="ihn-article-container">
        {/* ---------- Header ---------- */}
        <header className="ihn-article-header-block">
          <div className="ihn-article-meta">
            <span className="ihnBlog-expert__overlayMeta">
              <span className="ihnBlog-card__overlayAvatar">{article.author.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}</span>
              <span className="ihnBlog-card__overlayNameGroup">
                <span className="ihnBlog-card__overlayName">{article.author}</span>
                <span className="ihnBlog-card__overlayCompany">{article.publisher}</span>
              </span>
            </span>
<span className="ihn-article-meta-right">
              <span className="ihn-article-date">{formatArticleDate(article.date)}</span>
              <span className="ihn-article-more" ref={articleMenuRef}>
                <button
                  type="button"
                  className="ihn-article-more-btn"
                  aria-label="Article options"
                  aria-haspopup="menu"
                  aria-expanded={articleMenuOpen}
                  onClick={() => setArticleMenuOpen((v) => !v)}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                    <circle cx="12" cy="5" r="1.8" />
                    <circle cx="12" cy="12" r="1.8" />
                    <circle cx="12" cy="19" r="1.8" />
                  </svg>
                </button>
                {articleMenuOpen &&
                  (isOwnerArticle ? (
                    <span className="Community-dotmenu-dropdown" role="menu" aria-label="Article options">
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={() => {
                          setArticleMenuOpen(false);
                          navigate("/blog/create", {
                            state: { editBlog: article },
                          });
                        }}
                      >
                        <span aria-hidden="true">✏️</span>Edit
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleCopyArticleLink}
                      >
                        <span aria-hidden="true">🔗</span>
                        Copy link
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleSaveArticle}
                      >
                        <span aria-hidden="true" style={{ fontSize: "15px", lineHeight: 1 }}>⭐</span>{isSaved ? "Unsave" : "Save"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleArchiveArticle}
                      >
                        <span aria-hidden="true">📁</span>{isArchived ? "Unarchive" : "Archived"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleToggleHideLikes}
                      >
                        <span aria-hidden="true">{isLikeCountHidden ? "❤️" : "🚫"}</span>
                        {isLikeCountHidden ? "Show likes count" : "Hide likes count"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleToggleHideCommentCounts}
                      >
                        <span aria-hidden="true">{isCommentCountHidden ? "💬" : "🚫"}</span>
                        {isCommentCountHidden ? "Show comment count" : "Hide comment count"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleToggleLikesOff}
                      >
                        <span aria-hidden="true">❤️</span>
                        {likesOff ? "Turn on likes" : "Turn off likes"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleToggleCommentsOff}
                      >
                        <span aria-hidden="true">💬</span>
                        {commentsOff ? "Turn on comments" : "Turn off comments"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item Community-dotmenu-item-danger"
                        disabled={isDeleted}
                        onClick={handleDeleteArticle}
                      >
                        <span aria-hidden="true">🗑️</span>{isDeleted ? "Deleted" : "Delete"}
                      </button>
                    </span>
                  ) : (
                    <span className="Community-dotmenu-dropdown" role="menu" aria-label="Article options">
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleSaveArticle}
                      >
                        <span aria-hidden="true" style={{ fontSize: "15px", lineHeight: 1 }}>⭐</span>{isSaved ? "Unsave" : "Save"}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleCopyArticleLink}
                      >
                        <span aria-hidden="true">🔗</span>
                        Copy link
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item"
                        onClick={handleNotInterested}
                      >
                        <span aria-hidden="true">👎</span>
                        Not interested
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="Community-dotmenu-item Community-dotmenu-item-danger"
                        onClick={handleReportArticle}
                      >
                        <span aria-hidden="true">⚠️</span>
                        Report
                      </button>
                    </span>
                  ))}
              </span>
            </span>
          </div>

          <div className="ihn-article-heading-group">
            {(article.isExpertVoice || article.isTrending || article.isEditorsPickBadge) && (
              <span className="ihn-article-badges">
                {article.isEditorsPickBadge && (
                  <span className="ihn-article-pick-tag">Editor&rsquo;s Pick</span>
                )}
                {article.isExpertVoice && (
                  <span className="ihn-article-expert-tag">Expert Voices</span>
                )}
                {article.isTrending && (
                  <span className="ihn-article-trending-tag">Trending</span>
                )}
              </span>
            )}
            <p className="ihn-article-category">{article.category}</p>
            <h1 className="ihn-article-title">{article.title}</h1>
            {article.subhead ? (
              <p className="ihn-article-subhead">{article.subhead}</p>
            ) : (
              article.excerpt && (
                <p className="ihn-article-subhead">{article.excerpt}</p>
              )
            )}
          </div>
        </header>

        {/* ---------- Main image ---------- */}
        {article.hasLeadMedia !== false && (
        <div className="ihn-article-main-image-wrap">
          {article.mediaKinds && article.mediaKinds[0] === "video" ? (
            <BlogEditedVideo
              src={article.images[0]}
              edits={(article.videoEdits && article.videoEdits[0]) || null}
              poster={(article.videoThumbnails && article.videoThumbnails[0]) || ""}
            />
          ) : (
            <img
              className="ihn-article-main-image"
              src={article.images[0]}
              alt={`Lead image for "${article.title}"`}
              style={
                article.mediaAspects && article.mediaAspects[0]
                  ? { aspectRatio: `${article.mediaAspects[0]}`, height: "auto", objectFit: "fill" }
                  : undefined
              }
            />
          )}
        </div>
        )}

        {/* ---------- Body content ---------- */}
        <div className="ihn-article-body">
          {contentBlocks.map((block, i) => (
            <ArticleContentBlock
              key={i}
              block={block}
              images={article.images}
              aspects={article.mediaAspects}
            />
          ))}
        </div>

        {/* ---------- Like / comment / share ---------- */}
        <div className="ihn-article-actions">
          <ArticleEngagementBar
            article={article}
            onOpenComments={handleCommentClick}
            commentCount={liveCommentCount}
            hideLikeCount={isLikeCountHidden}
            hideCommentCount={isCommentCountHidden}
            likesOff={likesOff}
            commentsOff={commentsOff}
          />

          {commentsOpen && commentsOff && (
            <div className="ihn-article-comments-wrap" ref={commentsRef}>
              <div className="ihn-article-comments-disabled">
                <p>Comments are turned off for this post.</p>
              </div>
            </div>
          )}

          {commentsOpen && !commentsOff && (
            <div className="ihn-article-comments-wrap" ref={commentsRef}>
              <CommentsInline
                entity={{
                  id: entityId,
                  posterId: isOwnerArticle ? CURRENT_USER_ID : ARTICLE_POSTER_ID,
                  title: article.title,
                  defaultComments: commentSeeds,
                }}
              />
            </div>
          )}
        </div>

        {/* ---------- Writer profile icons ---------- */}
        {article.socials && article.socials.length > 0 && (
          <div className="ihn-article-connect">
            <div className="ihn-article-connect-row">
              {article.socials.map((s) => {
                const p = socialPlatformById(s.id);
                if (!p) return null;
                return (
                  <a
                    key={s.id}
                    className="Community-social-option"
                    href={socialHref(s)}
                    target="_blank"
                    rel="noreferrer"
                    title={`${p.label}: ${s.value}`}
                    aria-label={`Open ${p.label}`}
                  >
                    <span className="Community-social-option-icon">
                      {p.icon}
                    </span>
                  </a>
                );
              })}
            </div>
            <p className="ihn-article-connect-label">Let's Connect</p>
          </div>
        )}

        {/* ---------- Bottom meta: name left, byline right ---------- */}
        <div className="ihn-article-meta ihn-article-meta-bottom">
          <span className="ihnBlog-expert__overlayMeta">
            <span className="ihnBlog-card__overlayAvatar">{article.author.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}</span>
            <span className="ihnBlog-card__overlayNameGroup">
              <span className="ihnBlog-card__overlayName">{article.author}</span>
              <span className="ihnBlog-card__overlayCompany">{article.publisher}</span>
            </span>
          </span>
          <span className="ihn-article-date">{formatArticleDate(article.date)}</span>
        </div>
      </article>

      {toast && (
        <BlogToast
          key={toast.id}
          message={toast.message}
          onDismiss={() => setToast(null)}
        />
      )}

      {reportArticle && (
        <ReportPostModal
          post={{ id: reportArticle.id, posterId: ARTICLE_POSTER_ID }}
          reporterUserId={CURRENT_USER_ID}
          onClose={() => setReportArticle(null)}
          onSubmitReport={() => {}}
        />
      )}
    </div>
  );
}
